/**
 * This file was auto-generated.
 * Do not make direct changes to the file.
 */
/* eslint-disable @typescript-eslint/no-unused-vars */

import Axios, { AxiosError, AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios'
import { z } from 'zod'

import {
	Endpoint,
	EndpointInput,
	HttpMethod,
	EndpointRequestValidators,
	EndpointKnownErrorValidators,
	KnownErrorDefinitions,
	Paths,
	Methods,
	EndpointResponse,
	EndpointNoThrowResult,
	EndpointKnownError
} from './common'
export interface RequestInput {
	path?: Record<string, any>
	query?: Record<string, any>
	body?: any
}

export function splitQueryParamValue<T = string>(value: string | undefined): T[] {
	return (value ?? '')
		.split(',')
		.filter(x => !!x)
		.map(x => x.trim()) as T[]
}

export function joinQueryParamValue(values: string[] | undefined): string {
	return values?.filter(x => !!x).join(',') ?? ''
}

export function getAllRegexMatches(text: string, regex: RegExp): string[] {
	const result: string[] = []
	let matches: RegExpExecArray | null
	while ((matches = regex.exec(text))) {
		result.push(matches[1])
	}
	return result
}

export function pruneQuery(query: any) {
	if (!query) {
		return {}
	}
	const pruned: any = {}
	for (const [key, value] of Object.entries(query)) {
		if (value !== undefined) {
			pruned[key] = value
		}
	}
	return pruned
}

export function createUrl(path: string, input: any) {
	if (!input) {
		return path
	}
	if (input.path) {
		for (const [key, value] of Object.entries(input.path)) {
			path = path.replace(`:${key}`, value as string)
		}
	}
	const query = pruneQuery(input.query)
	if (Object.entries(query).length > 0) {
		const valuePairs = Object.entries(query).map(
			([key, value]) => `${key}=${encodeURIComponent((Array.isArray(value) ? value : [value]).join(','))}`
		)
		path += `?${valuePairs.join('&')}`
	}
	return path
}

export function validateUrl(endpoint: any, path: string, request: any) {
	try {
		const pathInput = { ...(request?.path ?? {}) }
		const queryInput = { ...(request?.query ?? {}) }
		const rawUrlParameters = { ...pathInput, ...pruneQuery(queryInput) }
		const urlParameters: any = {}
		for (const [key, value] of Object.entries(rawUrlParameters)) {
			if (value !== undefined) {
				urlParameters[key] = value
			}
		}
		const notFoundValues = Object.entries({ ...pathInput }).filter(
			([key, value]) => key !== 'storeId' && path.indexOf(`:${key}`) < 0
		)
		if (notFoundValues.length > 0) {
			throw new Error(
				`${endpoint}: Url parameters not found in url template: ${notFoundValues.map(([key]) => key).join(', ')}`
			)
		}

		const parameterRegex = /:(.*?)(\/|$|\?|&)/g
		const parameterMatches = getAllRegexMatches(path, parameterRegex)

		const unaccountedUrlParameters = parameterMatches.filter(
			parameterName => urlParameters[parameterName] === undefined
		)
		if (unaccountedUrlParameters.length > 0) {
			throw new Error(
				`${endpoint}: Url parameters found in url but not in parameter object: ${unaccountedUrlParameters
					.map(x => `:${x}`)
					.join(', ')}`
			)
		}
	} catch (e: any) {
		console.error(e)
		throw e
	}
}

export class SentryTag {
	constructor(
		public key: string,
		public value: string
	) {}
}

export const isApiError = (e: any): e is ApiError => {
	return e?._isApiError
}

export const isKnownApiError = (e: unknown): boolean => {
	if (!isApiError(e)) {
		return false
	}
	const type = (e.data as { type?: unknown } | undefined)?.type
	return typeof type === 'string' && type in KnownErrorDefinitions
}

export class ApiError extends Error {
	readonly _isApiError = true
	title?: string
	httpStatusCode = 0
	errorType = ''
	data: any
	endpoint = ''
	extras: any = {}
	tags: SentryTag[] = []
	debug?: {
		stack?: string
		extras?: unknown
		sentryEventId?: string
		requestId?: string
	}
	apiEventId = ''

	addExtras(input: any) {
		this.extras = { ...this.extras, ...input }
		return this
	}

	addTag(...tags: SentryTag[]) {
		this.tags.push(...tags)
		return this
	}

	static from(endpoint: string, e: AxiosError, stack?: string) {
		const error = new ApiError(`Error in endpoint: ${endpoint}`)
		if (stack) {
			error.stack = stack
		}
		error.endpoint = endpoint
		error.addTag(new SentryTag('endpoint', endpoint))
		if (e.response) {
			error.httpStatusCode = e.response?.status
			error.errorType = (e.response?.data as any)?.errorType ?? (e.response?.data as any)?.error
			error.data = e.response?.data
			const body = e.response.data as { debug?: ApiError['debug'] } | undefined
			if (body?.debug) {
				error.debug = body.debug
			}
			const sentryId = e.response.headers?.['sentry-id']
			if (typeof sentryId === 'string') {
				error.apiEventId = sentryId
			} else if (body?.debug?.sentryEventId) {
				error.apiEventId = body.debug.sentryEventId
			}
		}
		return error
	}
}

/**
 * Extract a typed known error from a thrown ApiError for the given endpoint.
 * Returns undefined when the body is not a declared known error (generic 4xx/5xx, network, etc.).
 */
export function parseKnownError<T extends Endpoint>(err: unknown, endpoint: T): EndpointKnownError<T> | undefined {
	if (!isApiError(err)) {
		return undefined
	}
	const validator = (EndpointKnownErrorValidators as Record<string, z.ZodTypeAny | undefined>)[endpoint]
	if (!validator) {
		return undefined
	}
	const parsed = validator.safeParse(err.data)
	if (!parsed.success) {
		return undefined
	}
	const body = parsed.data as { type: string; data?: unknown; message?: string }
	const def = (KnownErrorDefinitions as Record<string, { status: number } | undefined>)[body.type]
	if (!def || def.status !== err.httpStatusCode) {
		return undefined
	}
	return {
		isSuccess: false,
		status: err.httpStatusCode,
		type: body.type,
		data: body.data,
		message: body.message
	} as unknown as EndpointKnownError<T>
}

export type HttpMethodType = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

export type MakeRequestOptions = {
	throwOnError?: boolean
}

export class BaseApi {
	constructor(public client: AxiosInstance) {}

	protected async checkIfEndpointNeedsToWait(endpoint: string): Promise<void> {
		return
	}

	protected async internalMakeRequest<TInput, TResponse>(
		input: TInput,
		endpoint: string,
		method: HttpMethodType,
		path: string,
		options?: MakeRequestOptions
	) {
		validateUrl(endpoint, path, input)
		const substitutedPath = createUrl(path, input)

		let result: AxiosResponse<TResponse> | undefined = undefined

		await this.checkIfEndpointNeedsToWait(endpoint)

		const requestConfig: AxiosRequestConfig = {
			validateStatus: status => {
				if (options?.throwOnError) {
					return status >= 200 && status < 300
				}
				return true
			},
			headers: {
				...(!(input as any)?.body
					? {
							'Content-Type': 'text/plain'
						}
					: {}),
				...((input as any)?.headers ?? {})
			}
		}
		switch (method) {
			case 'GET':
				result = await this.client.get<TResponse>(substitutedPath, requestConfig)
				break
			case 'POST':
				result = await this.client.post<TResponse>(substitutedPath, (input as any)?.body, requestConfig)
				break
			case 'PUT':
				result = await this.client.put<TResponse>(substitutedPath, (input as any)?.body, requestConfig)
				break
			case 'DELETE':
				result = await this.client.delete<TResponse>(substitutedPath, requestConfig)
				break
			default:
				throw new Error(`Unsupported method: ${method}`)
		}
		if (!result) {
			throw new Error(`No result from ${method} ${substitutedPath}`)
		}
		return { status: result?.status, data: result?.data, raw: result }
	}
}

export class Api extends BaseApi {
	protected createUrl<T extends Endpoint>(endpoint: T, input: EndpointInput<T>) {
		const path = Paths[endpoint]
		validateUrl(endpoint, path, input)
		return createUrl(path, input)
	}

	protected async onBeforeRequest<T extends Endpoint>(data: {
		endpoint: T
		input: any
		method: HttpMethod
		path: string
	}) {}

	protected async onAfterResponse<T extends Endpoint>(
		data: { endpoint: T; input: any; method: HttpMethod; path: string },
		response: { status: number; data: EndpointResponse<T> }
	) {}

	async makeRequest<T extends Endpoint>(endpoint: T, input: EndpointInput<T>, options?: MakeRequestOptions) {
		const stackTrace = new Error().stack
		try {
			const parseResult = EndpointRequestValidators[endpoint].input.safeParse(input)
			if (!parseResult.success) {
				console.error(`Invalid input for ${endpoint}`, parseResult.error.format())
				if (options?.throwOnError) {
					throw parseResult.error
				}
				return {
					isSuccess: false,
					status: 400,
					data: parseResult.error.format() as unknown as EndpointResponse<T>,
					raw: undefined
				}
			}
			const parsed = parseResult.data
			const path = Paths[endpoint]
			const method = Methods[endpoint] as HttpMethod
			await this.onBeforeRequest({ endpoint, input: parsed, method, path })
			const result = await this.internalMakeRequest<EndpointInput<T>, EndpointResponse<T>>(
				parsed as EndpointInput<T>,
				endpoint,
				method,
				path,
				options
			)
			await this.onAfterResponse({ endpoint, input: parsed, method, path }, result)
			if (!result) {
				throw new Error('Received undefined response')
			}
			return result
		} catch (e) {
			if (Axios.isAxiosError(e)) {
				const error = ApiError.from(endpoint, e, stackTrace)
				error.addExtras({
					axiosCode: e.code,
					cause: e.cause,
					request: JSON.stringify(input, null, 2),
					requestId: e.config?.headers['request-id'],
					baseUrl: e.config?.baseURL,
					url: e.config?.url
				})
				if (e.response) {
					const response = e.response.data
					error.addExtras({
						response: JSON.stringify(response ?? '', null, 2),
						request: JSON.stringify(input, null, 2),
						requestId: e.config?.headers['request-id'],
						headers: e.response.headers,
						statusCode: e.response.status,
						url: e.config?.url
					})
				}
				console.error(error.message, JSON.stringify(error, null, 2))
				throw error
			} else {
				throw e
			}
		}
		throw new Error('Request failed for unknown reason, should never happen')
	}

	async makeRequestWithThrow<T extends Endpoint>(endpoint: T, input: EndpointInput<T>) {
		const result = await this.makeRequest(endpoint, input, { throwOnError: true })
		return result.data
	}
	async makeRequestNoThrow<T extends Endpoint>(
		endpoint: T,
		input: EndpointInput<T>
	): Promise<EndpointNoThrowResult<T>> {
		try {
			const result = await this.makeRequest(endpoint, input, { throwOnError: false })
			if (result.status >= 200 && result.status < 300) {
				return { isSuccess: true, status: result.status, response: result.data, raw: result.raw } as any
			}
			const knownValidator = (EndpointKnownErrorValidators as Record<string, z.ZodTypeAny>)[endpoint]
			if (knownValidator) {
				const parsed = knownValidator.safeParse(result.data)
				if (parsed.success) {
					const body = parsed.data as { type: string; data?: unknown; message?: string }
					const def = (KnownErrorDefinitions as Record<string, { status: number }>)[body.type]
					if (def && def.status === result.status) {
						return {
							isSuccess: false,
							status: result.status,
							type: body.type,
							data: body.data,
							message: body.message
						} as any
					}
				}
				return { isSuccess: false, status: result.status, type: 'UNKNOWN' } as any
			}
			return { isSuccess: false, status: result.status, response: result.data, raw: result.raw } as any
		} catch (e: any) {
			const knownValidator = (EndpointKnownErrorValidators as Record<string, z.ZodTypeAny>)[endpoint]
			if (knownValidator) {
				return { isSuccess: false, status: 0, type: 'UNKNOWN', error: e } as any
			}
			return { isSuccess: false, status: 0, response: e?.data as any, error: e } as any
		}
	}

	async getApiKey(input: EndpointInput<Endpoint.GetApiKey>) {
		return this.makeRequestWithThrow(Endpoint.GetApiKey, input)
	}
	async getApiKeyNoThrow(input: EndpointInput<Endpoint.GetApiKey>) {
		return this.makeRequestNoThrow(Endpoint.GetApiKey, input)
	}

	async login(input: EndpointInput<Endpoint.Login>) {
		return this.makeRequestWithThrow(Endpoint.Login, input)
	}
	async loginNoThrow(input: EndpointInput<Endpoint.Login>) {
		return this.makeRequestNoThrow(Endpoint.Login, input)
	}

	async register(input: EndpointInput<Endpoint.Register>) {
		return this.makeRequestWithThrow(Endpoint.Register, input)
	}
	async registerNoThrow(input: EndpointInput<Endpoint.Register>) {
		return this.makeRequestNoThrow(Endpoint.Register, input)
	}

	async resetPassword(input: EndpointInput<Endpoint.ResetPassword>) {
		return this.makeRequestWithThrow(Endpoint.ResetPassword, input)
	}
	async resetPasswordNoThrow(input: EndpointInput<Endpoint.ResetPassword>) {
		return this.makeRequestNoThrow(Endpoint.ResetPassword, input)
	}

	async updatePassword(input: EndpointInput<Endpoint.UpdatePassword>) {
		return this.makeRequestWithThrow(Endpoint.UpdatePassword, input)
	}
	async updatePasswordNoThrow(input: EndpointInput<Endpoint.UpdatePassword>) {
		return this.makeRequestNoThrow(Endpoint.UpdatePassword, input)
	}

	async listUsers(input: EndpointInput<Endpoint.ListUsers>) {
		return this.makeRequestWithThrow(Endpoint.ListUsers, input)
	}
	async listUsersNoThrow(input: EndpointInput<Endpoint.ListUsers>) {
		return this.makeRequestNoThrow(Endpoint.ListUsers, input)
	}

	async getMe(input: EndpointInput<Endpoint.GetMe>) {
		return this.makeRequestWithThrow(Endpoint.GetMe, input)
	}
	async getMeNoThrow(input: EndpointInput<Endpoint.GetMe>) {
		return this.makeRequestNoThrow(Endpoint.GetMe, input)
	}

	async updateMe(input: EndpointInput<Endpoint.UpdateMe>) {
		return this.makeRequestWithThrow(Endpoint.UpdateMe, input)
	}
	async updateMeNoThrow(input: EndpointInput<Endpoint.UpdateMe>) {
		return this.makeRequestNoThrow(Endpoint.UpdateMe, input)
	}

	async validateMySignInPin(input: EndpointInput<Endpoint.ValidateMySignInPin>) {
		return this.makeRequestWithThrow(Endpoint.ValidateMySignInPin, input)
	}
	async validateMySignInPinNoThrow(input: EndpointInput<Endpoint.ValidateMySignInPin>) {
		return this.makeRequestNoThrow(Endpoint.ValidateMySignInPin, input)
	}

	async setMySignInPin(input: EndpointInput<Endpoint.SetMySignInPin>) {
		return this.makeRequestWithThrow(Endpoint.SetMySignInPin, input)
	}
	async setMySignInPinNoThrow(input: EndpointInput<Endpoint.SetMySignInPin>) {
		return this.makeRequestNoThrow(Endpoint.SetMySignInPin, input)
	}

	async getUser(input: EndpointInput<Endpoint.GetUser>) {
		return this.makeRequestWithThrow(Endpoint.GetUser, input)
	}
	async getUserNoThrow(input: EndpointInput<Endpoint.GetUser>) {
		return this.makeRequestNoThrow(Endpoint.GetUser, input)
	}

	async updateUser(input: EndpointInput<Endpoint.UpdateUser>) {
		return this.makeRequestWithThrow(Endpoint.UpdateUser, input)
	}
	async updateUserNoThrow(input: EndpointInput<Endpoint.UpdateUser>) {
		return this.makeRequestNoThrow(Endpoint.UpdateUser, input)
	}

	async deleteUser(input: EndpointInput<Endpoint.DeleteUser>) {
		return this.makeRequestWithThrow(Endpoint.DeleteUser, input)
	}
	async deleteUserNoThrow(input: EndpointInput<Endpoint.DeleteUser>) {
		return this.makeRequestNoThrow(Endpoint.DeleteUser, input)
	}

	async getUserSignInCredentials(input: EndpointInput<Endpoint.GetUserSignInCredentials>) {
		return this.makeRequestWithThrow(Endpoint.GetUserSignInCredentials, input)
	}
	async getUserSignInCredentialsNoThrow(input: EndpointInput<Endpoint.GetUserSignInCredentials>) {
		return this.makeRequestNoThrow(Endpoint.GetUserSignInCredentials, input)
	}

	async setUserSignInCredentials(input: EndpointInput<Endpoint.SetUserSignInCredentials>) {
		return this.makeRequestWithThrow(Endpoint.SetUserSignInCredentials, input)
	}
	async setUserSignInCredentialsNoThrow(input: EndpointInput<Endpoint.SetUserSignInCredentials>) {
		return this.makeRequestNoThrow(Endpoint.SetUserSignInCredentials, input)
	}

	async listInvites(input: EndpointInput<Endpoint.ListInvites>) {
		return this.makeRequestWithThrow(Endpoint.ListInvites, input)
	}
	async listInvitesNoThrow(input: EndpointInput<Endpoint.ListInvites>) {
		return this.makeRequestNoThrow(Endpoint.ListInvites, input)
	}

	async createInvite(input: EndpointInput<Endpoint.CreateInvite>) {
		return this.makeRequestWithThrow(Endpoint.CreateInvite, input)
	}
	async createInviteNoThrow(input: EndpointInput<Endpoint.CreateInvite>) {
		return this.makeRequestNoThrow(Endpoint.CreateInvite, input)
	}

	async deleteInvite(input: EndpointInput<Endpoint.DeleteInvite>) {
		return this.makeRequestWithThrow(Endpoint.DeleteInvite, input)
	}
	async deleteInviteNoThrow(input: EndpointInput<Endpoint.DeleteInvite>) {
		return this.makeRequestNoThrow(Endpoint.DeleteInvite, input)
	}

	async listSalesChannelTypes(input: EndpointInput<Endpoint.ListSalesChannelTypes>) {
		return this.makeRequestWithThrow(Endpoint.ListSalesChannelTypes, input)
	}
	async listSalesChannelTypesNoThrow(input: EndpointInput<Endpoint.ListSalesChannelTypes>) {
		return this.makeRequestNoThrow(Endpoint.ListSalesChannelTypes, input)
	}

	async createSalesChannelType(input: EndpointInput<Endpoint.CreateSalesChannelType>) {
		return this.makeRequestWithThrow(Endpoint.CreateSalesChannelType, input)
	}
	async createSalesChannelTypeNoThrow(input: EndpointInput<Endpoint.CreateSalesChannelType>) {
		return this.makeRequestNoThrow(Endpoint.CreateSalesChannelType, input)
	}

	async getSalesChannelType(input: EndpointInput<Endpoint.GetSalesChannelType>) {
		return this.makeRequestWithThrow(Endpoint.GetSalesChannelType, input)
	}
	async getSalesChannelTypeNoThrow(input: EndpointInput<Endpoint.GetSalesChannelType>) {
		return this.makeRequestNoThrow(Endpoint.GetSalesChannelType, input)
	}

	async updateSalesChannelType(input: EndpointInput<Endpoint.UpdateSalesChannelType>) {
		return this.makeRequestWithThrow(Endpoint.UpdateSalesChannelType, input)
	}
	async updateSalesChannelTypeNoThrow(input: EndpointInput<Endpoint.UpdateSalesChannelType>) {
		return this.makeRequestNoThrow(Endpoint.UpdateSalesChannelType, input)
	}

	async deleteSalesChannelType(input: EndpointInput<Endpoint.DeleteSalesChannelType>) {
		return this.makeRequestWithThrow(Endpoint.DeleteSalesChannelType, input)
	}
	async deleteSalesChannelTypeNoThrow(input: EndpointInput<Endpoint.DeleteSalesChannelType>) {
		return this.makeRequestNoThrow(Endpoint.DeleteSalesChannelType, input)
	}

	async listSalesChannelGroups(input: EndpointInput<Endpoint.ListSalesChannelGroups>) {
		return this.makeRequestWithThrow(Endpoint.ListSalesChannelGroups, input)
	}
	async listSalesChannelGroupsNoThrow(input: EndpointInput<Endpoint.ListSalesChannelGroups>) {
		return this.makeRequestNoThrow(Endpoint.ListSalesChannelGroups, input)
	}

	async createSalesChannelGroup(input: EndpointInput<Endpoint.CreateSalesChannelGroup>) {
		return this.makeRequestWithThrow(Endpoint.CreateSalesChannelGroup, input)
	}
	async createSalesChannelGroupNoThrow(input: EndpointInput<Endpoint.CreateSalesChannelGroup>) {
		return this.makeRequestNoThrow(Endpoint.CreateSalesChannelGroup, input)
	}

	async getSalesChannelGroup(input: EndpointInput<Endpoint.GetSalesChannelGroup>) {
		return this.makeRequestWithThrow(Endpoint.GetSalesChannelGroup, input)
	}
	async getSalesChannelGroupNoThrow(input: EndpointInput<Endpoint.GetSalesChannelGroup>) {
		return this.makeRequestNoThrow(Endpoint.GetSalesChannelGroup, input)
	}

	async updateSalesChannelGroup(input: EndpointInput<Endpoint.UpdateSalesChannelGroup>) {
		return this.makeRequestWithThrow(Endpoint.UpdateSalesChannelGroup, input)
	}
	async updateSalesChannelGroupNoThrow(input: EndpointInput<Endpoint.UpdateSalesChannelGroup>) {
		return this.makeRequestNoThrow(Endpoint.UpdateSalesChannelGroup, input)
	}

	async deleteSalesChannelGroup(input: EndpointInput<Endpoint.DeleteSalesChannelGroup>) {
		return this.makeRequestWithThrow(Endpoint.DeleteSalesChannelGroup, input)
	}
	async deleteSalesChannelGroupNoThrow(input: EndpointInput<Endpoint.DeleteSalesChannelGroup>) {
		return this.makeRequestNoThrow(Endpoint.DeleteSalesChannelGroup, input)
	}

	async listSalesChannels(input: EndpointInput<Endpoint.ListSalesChannels>) {
		return this.makeRequestWithThrow(Endpoint.ListSalesChannels, input)
	}
	async listSalesChannelsNoThrow(input: EndpointInput<Endpoint.ListSalesChannels>) {
		return this.makeRequestNoThrow(Endpoint.ListSalesChannels, input)
	}

	async createSalesChannel(input: EndpointInput<Endpoint.CreateSalesChannel>) {
		return this.makeRequestWithThrow(Endpoint.CreateSalesChannel, input)
	}
	async createSalesChannelNoThrow(input: EndpointInput<Endpoint.CreateSalesChannel>) {
		return this.makeRequestNoThrow(Endpoint.CreateSalesChannel, input)
	}

	async getSalesChannel(input: EndpointInput<Endpoint.GetSalesChannel>) {
		return this.makeRequestWithThrow(Endpoint.GetSalesChannel, input)
	}
	async getSalesChannelNoThrow(input: EndpointInput<Endpoint.GetSalesChannel>) {
		return this.makeRequestNoThrow(Endpoint.GetSalesChannel, input)
	}

	async updateSalesChannel(input: EndpointInput<Endpoint.UpdateSalesChannel>) {
		return this.makeRequestWithThrow(Endpoint.UpdateSalesChannel, input)
	}
	async updateSalesChannelNoThrow(input: EndpointInput<Endpoint.UpdateSalesChannel>) {
		return this.makeRequestNoThrow(Endpoint.UpdateSalesChannel, input)
	}

	async deleteSalesChannel(input: EndpointInput<Endpoint.DeleteSalesChannel>) {
		return this.makeRequestWithThrow(Endpoint.DeleteSalesChannel, input)
	}
	async deleteSalesChannelNoThrow(input: EndpointInput<Endpoint.DeleteSalesChannel>) {
		return this.makeRequestNoThrow(Endpoint.DeleteSalesChannel, input)
	}

	async listPointOfSaleGroups(input: EndpointInput<Endpoint.ListPointOfSaleGroups>) {
		return this.makeRequestWithThrow(Endpoint.ListPointOfSaleGroups, input)
	}
	async listPointOfSaleGroupsNoThrow(input: EndpointInput<Endpoint.ListPointOfSaleGroups>) {
		return this.makeRequestNoThrow(Endpoint.ListPointOfSaleGroups, input)
	}

	async createPointOfSaleGroup(input: EndpointInput<Endpoint.CreatePointOfSaleGroup>) {
		return this.makeRequestWithThrow(Endpoint.CreatePointOfSaleGroup, input)
	}
	async createPointOfSaleGroupNoThrow(input: EndpointInput<Endpoint.CreatePointOfSaleGroup>) {
		return this.makeRequestNoThrow(Endpoint.CreatePointOfSaleGroup, input)
	}

	async getPointOfSaleGroup(input: EndpointInput<Endpoint.GetPointOfSaleGroup>) {
		return this.makeRequestWithThrow(Endpoint.GetPointOfSaleGroup, input)
	}
	async getPointOfSaleGroupNoThrow(input: EndpointInput<Endpoint.GetPointOfSaleGroup>) {
		return this.makeRequestNoThrow(Endpoint.GetPointOfSaleGroup, input)
	}

	async updatePointOfSaleGroup(input: EndpointInput<Endpoint.UpdatePointOfSaleGroup>) {
		return this.makeRequestWithThrow(Endpoint.UpdatePointOfSaleGroup, input)
	}
	async updatePointOfSaleGroupNoThrow(input: EndpointInput<Endpoint.UpdatePointOfSaleGroup>) {
		return this.makeRequestNoThrow(Endpoint.UpdatePointOfSaleGroup, input)
	}

	async deletePointOfSaleGroup(input: EndpointInput<Endpoint.DeletePointOfSaleGroup>) {
		return this.makeRequestWithThrow(Endpoint.DeletePointOfSaleGroup, input)
	}
	async deletePointOfSaleGroupNoThrow(input: EndpointInput<Endpoint.DeletePointOfSaleGroup>) {
		return this.makeRequestNoThrow(Endpoint.DeletePointOfSaleGroup, input)
	}

	async listPointsOfSale(input: EndpointInput<Endpoint.ListPointsOfSale>) {
		return this.makeRequestWithThrow(Endpoint.ListPointsOfSale, input)
	}
	async listPointsOfSaleNoThrow(input: EndpointInput<Endpoint.ListPointsOfSale>) {
		return this.makeRequestNoThrow(Endpoint.ListPointsOfSale, input)
	}

	async createPointOfSale(input: EndpointInput<Endpoint.CreatePointOfSale>) {
		return this.makeRequestWithThrow(Endpoint.CreatePointOfSale, input)
	}
	async createPointOfSaleNoThrow(input: EndpointInput<Endpoint.CreatePointOfSale>) {
		return this.makeRequestNoThrow(Endpoint.CreatePointOfSale, input)
	}

	async getPointOfSaleHierarchy(input: EndpointInput<Endpoint.GetPointOfSaleHierarchy>) {
		return this.makeRequestWithThrow(Endpoint.GetPointOfSaleHierarchy, input)
	}
	async getPointOfSaleHierarchyNoThrow(input: EndpointInput<Endpoint.GetPointOfSaleHierarchy>) {
		return this.makeRequestNoThrow(Endpoint.GetPointOfSaleHierarchy, input)
	}

	async getCurrentPointOfSale(input: EndpointInput<Endpoint.GetCurrentPointOfSale>) {
		return this.makeRequestWithThrow(Endpoint.GetCurrentPointOfSale, input)
	}
	async getCurrentPointOfSaleNoThrow(input: EndpointInput<Endpoint.GetCurrentPointOfSale>) {
		return this.makeRequestNoThrow(Endpoint.GetCurrentPointOfSale, input)
	}

	async createPointOfSaleWebSocketTicket(input: EndpointInput<Endpoint.CreatePointOfSaleWebSocketTicket>) {
		return this.makeRequestWithThrow(Endpoint.CreatePointOfSaleWebSocketTicket, input)
	}
	async createPointOfSaleWebSocketTicketNoThrow(input: EndpointInput<Endpoint.CreatePointOfSaleWebSocketTicket>) {
		return this.makeRequestNoThrow(Endpoint.CreatePointOfSaleWebSocketTicket, input)
	}

	async getPointOfSale(input: EndpointInput<Endpoint.GetPointOfSale>) {
		return this.makeRequestWithThrow(Endpoint.GetPointOfSale, input)
	}
	async getPointOfSaleNoThrow(input: EndpointInput<Endpoint.GetPointOfSale>) {
		return this.makeRequestNoThrow(Endpoint.GetPointOfSale, input)
	}

	async updatePointOfSale(input: EndpointInput<Endpoint.UpdatePointOfSale>) {
		return this.makeRequestWithThrow(Endpoint.UpdatePointOfSale, input)
	}
	async updatePointOfSaleNoThrow(input: EndpointInput<Endpoint.UpdatePointOfSale>) {
		return this.makeRequestNoThrow(Endpoint.UpdatePointOfSale, input)
	}

	async openCooler(input: EndpointInput<Endpoint.OpenCooler>) {
		return this.makeRequestWithThrow(Endpoint.OpenCooler, input)
	}
	async openCoolerNoThrow(input: EndpointInput<Endpoint.OpenCooler>) {
		return this.makeRequestNoThrow(Endpoint.OpenCooler, input)
	}

	async openCashFloat(input: EndpointInput<Endpoint.OpenCashFloat>) {
		return this.makeRequestWithThrow(Endpoint.OpenCashFloat, input)
	}
	async openCashFloatNoThrow(input: EndpointInput<Endpoint.OpenCashFloat>) {
		return this.makeRequestNoThrow(Endpoint.OpenCashFloat, input)
	}

	async cashPayIn(input: EndpointInput<Endpoint.CashPayIn>) {
		return this.makeRequestWithThrow(Endpoint.CashPayIn, input)
	}
	async cashPayInNoThrow(input: EndpointInput<Endpoint.CashPayIn>) {
		return this.makeRequestNoThrow(Endpoint.CashPayIn, input)
	}

	async cashPayOut(input: EndpointInput<Endpoint.CashPayOut>) {
		return this.makeRequestWithThrow(Endpoint.CashPayOut, input)
	}
	async cashPayOutNoThrow(input: EndpointInput<Endpoint.CashPayOut>) {
		return this.makeRequestNoThrow(Endpoint.CashPayOut, input)
	}

	async cashCount(input: EndpointInput<Endpoint.CashCount>) {
		return this.makeRequestWithThrow(Endpoint.CashCount, input)
	}
	async cashCountNoThrow(input: EndpointInput<Endpoint.CashCount>) {
		return this.makeRequestNoThrow(Endpoint.CashCount, input)
	}

	async getPointOfSaleDeviceStatus(input: EndpointInput<Endpoint.GetPointOfSaleDeviceStatus>) {
		return this.makeRequestWithThrow(Endpoint.GetPointOfSaleDeviceStatus, input)
	}
	async getPointOfSaleDeviceStatusNoThrow(input: EndpointInput<Endpoint.GetPointOfSaleDeviceStatus>) {
		return this.makeRequestNoThrow(Endpoint.GetPointOfSaleDeviceStatus, input)
	}

	async refreshPointOfSaleDeviceStatus(input: EndpointInput<Endpoint.RefreshPointOfSaleDeviceStatus>) {
		return this.makeRequestWithThrow(Endpoint.RefreshPointOfSaleDeviceStatus, input)
	}
	async refreshPointOfSaleDeviceStatusNoThrow(input: EndpointInput<Endpoint.RefreshPointOfSaleDeviceStatus>) {
		return this.makeRequestNoThrow(Endpoint.RefreshPointOfSaleDeviceStatus, input)
	}

	async lookupDeviceEnrollmentByCode(input: EndpointInput<Endpoint.LookupDeviceEnrollmentByCode>) {
		return this.makeRequestWithThrow(Endpoint.LookupDeviceEnrollmentByCode, input)
	}
	async lookupDeviceEnrollmentByCodeNoThrow(input: EndpointInput<Endpoint.LookupDeviceEnrollmentByCode>) {
		return this.makeRequestNoThrow(Endpoint.LookupDeviceEnrollmentByCode, input)
	}

	async enrollPointOfSaleDevice(input: EndpointInput<Endpoint.EnrollPointOfSaleDevice>) {
		return this.makeRequestWithThrow(Endpoint.EnrollPointOfSaleDevice, input)
	}
	async enrollPointOfSaleDeviceNoThrow(input: EndpointInput<Endpoint.EnrollPointOfSaleDevice>) {
		return this.makeRequestNoThrow(Endpoint.EnrollPointOfSaleDevice, input)
	}

	async unenrollPointOfSaleDevice(input: EndpointInput<Endpoint.UnenrollPointOfSaleDevice>) {
		return this.makeRequestWithThrow(Endpoint.UnenrollPointOfSaleDevice, input)
	}
	async unenrollPointOfSaleDeviceNoThrow(input: EndpointInput<Endpoint.UnenrollPointOfSaleDevice>) {
		return this.makeRequestNoThrow(Endpoint.UnenrollPointOfSaleDevice, input)
	}

	async notifyPointOfSaleAppUpdate(input: EndpointInput<Endpoint.NotifyPointOfSaleAppUpdate>) {
		return this.makeRequestWithThrow(Endpoint.NotifyPointOfSaleAppUpdate, input)
	}
	async notifyPointOfSaleAppUpdateNoThrow(input: EndpointInput<Endpoint.NotifyPointOfSaleAppUpdate>) {
		return this.makeRequestNoThrow(Endpoint.NotifyPointOfSaleAppUpdate, input)
	}

	async restartPointOfSaleApp(input: EndpointInput<Endpoint.RestartPointOfSaleApp>) {
		return this.makeRequestWithThrow(Endpoint.RestartPointOfSaleApp, input)
	}
	async restartPointOfSaleAppNoThrow(input: EndpointInput<Endpoint.RestartPointOfSaleApp>) {
		return this.makeRequestNoThrow(Endpoint.RestartPointOfSaleApp, input)
	}

	async lockPointOfSaleKiosk(input: EndpointInput<Endpoint.LockPointOfSaleKiosk>) {
		return this.makeRequestWithThrow(Endpoint.LockPointOfSaleKiosk, input)
	}
	async lockPointOfSaleKioskNoThrow(input: EndpointInput<Endpoint.LockPointOfSaleKiosk>) {
		return this.makeRequestNoThrow(Endpoint.LockPointOfSaleKiosk, input)
	}

	async unlockPointOfSaleKiosk(input: EndpointInput<Endpoint.UnlockPointOfSaleKiosk>) {
		return this.makeRequestWithThrow(Endpoint.UnlockPointOfSaleKiosk, input)
	}
	async unlockPointOfSaleKioskNoThrow(input: EndpointInput<Endpoint.UnlockPointOfSaleKiosk>) {
		return this.makeRequestNoThrow(Endpoint.UnlockPointOfSaleKiosk, input)
	}

	async unlockPointOfSaleDoor(input: EndpointInput<Endpoint.UnlockPointOfSaleDoor>) {
		return this.makeRequestWithThrow(Endpoint.UnlockPointOfSaleDoor, input)
	}
	async unlockPointOfSaleDoorNoThrow(input: EndpointInput<Endpoint.UnlockPointOfSaleDoor>) {
		return this.makeRequestNoThrow(Endpoint.UnlockPointOfSaleDoor, input)
	}

	async getGmsPlayTracks(input: EndpointInput<Endpoint.GetGmsPlayTracks>) {
		return this.makeRequestWithThrow(Endpoint.GetGmsPlayTracks, input)
	}
	async getGmsPlayTracksNoThrow(input: EndpointInput<Endpoint.GetGmsPlayTracks>) {
		return this.makeRequestNoThrow(Endpoint.GetGmsPlayTracks, input)
	}

	async listPublishableKeys(input: EndpointInput<Endpoint.ListPublishableKeys>) {
		return this.makeRequestWithThrow(Endpoint.ListPublishableKeys, input)
	}
	async listPublishableKeysNoThrow(input: EndpointInput<Endpoint.ListPublishableKeys>) {
		return this.makeRequestNoThrow(Endpoint.ListPublishableKeys, input)
	}

	async createPublishableKey(input: EndpointInput<Endpoint.CreatePublishableKey>) {
		return this.makeRequestWithThrow(Endpoint.CreatePublishableKey, input)
	}
	async createPublishableKeyNoThrow(input: EndpointInput<Endpoint.CreatePublishableKey>) {
		return this.makeRequestNoThrow(Endpoint.CreatePublishableKey, input)
	}

	async getPublishableKey(input: EndpointInput<Endpoint.GetPublishableKey>) {
		return this.makeRequestWithThrow(Endpoint.GetPublishableKey, input)
	}
	async getPublishableKeyNoThrow(input: EndpointInput<Endpoint.GetPublishableKey>) {
		return this.makeRequestNoThrow(Endpoint.GetPublishableKey, input)
	}

	async updatePublishableKey(input: EndpointInput<Endpoint.UpdatePublishableKey>) {
		return this.makeRequestWithThrow(Endpoint.UpdatePublishableKey, input)
	}
	async updatePublishableKeyNoThrow(input: EndpointInput<Endpoint.UpdatePublishableKey>) {
		return this.makeRequestNoThrow(Endpoint.UpdatePublishableKey, input)
	}

	async deletePublishableKey(input: EndpointInput<Endpoint.DeletePublishableKey>) {
		return this.makeRequestWithThrow(Endpoint.DeletePublishableKey, input)
	}
	async deletePublishableKeyNoThrow(input: EndpointInput<Endpoint.DeletePublishableKey>) {
		return this.makeRequestNoThrow(Endpoint.DeletePublishableKey, input)
	}

	async createSecretKey(input: EndpointInput<Endpoint.CreateSecretKey>) {
		return this.makeRequestWithThrow(Endpoint.CreateSecretKey, input)
	}
	async createSecretKeyNoThrow(input: EndpointInput<Endpoint.CreateSecretKey>) {
		return this.makeRequestNoThrow(Endpoint.CreateSecretKey, input)
	}

	async updateSecretKey(input: EndpointInput<Endpoint.UpdateSecretKey>) {
		return this.makeRequestWithThrow(Endpoint.UpdateSecretKey, input)
	}
	async updateSecretKeyNoThrow(input: EndpointInput<Endpoint.UpdateSecretKey>) {
		return this.makeRequestNoThrow(Endpoint.UpdateSecretKey, input)
	}

	async listDevices(input: EndpointInput<Endpoint.ListDevices>) {
		return this.makeRequestWithThrow(Endpoint.ListDevices, input)
	}
	async listDevicesNoThrow(input: EndpointInput<Endpoint.ListDevices>) {
		return this.makeRequestNoThrow(Endpoint.ListDevices, input)
	}

	async createDevice(input: EndpointInput<Endpoint.CreateDevice>) {
		return this.makeRequestWithThrow(Endpoint.CreateDevice, input)
	}
	async createDeviceNoThrow(input: EndpointInput<Endpoint.CreateDevice>) {
		return this.makeRequestNoThrow(Endpoint.CreateDevice, input)
	}

	async getCurrentDevice(input: EndpointInput<Endpoint.GetCurrentDevice>) {
		return this.makeRequestWithThrow(Endpoint.GetCurrentDevice, input)
	}
	async getCurrentDeviceNoThrow(input: EndpointInput<Endpoint.GetCurrentDevice>) {
		return this.makeRequestNoThrow(Endpoint.GetCurrentDevice, input)
	}

	async getDeviceInfo(input: EndpointInput<Endpoint.GetDeviceInfo>) {
		return this.makeRequestWithThrow(Endpoint.GetDeviceInfo, input)
	}
	async getDeviceInfoNoThrow(input: EndpointInput<Endpoint.GetDeviceInfo>) {
		return this.makeRequestNoThrow(Endpoint.GetDeviceInfo, input)
	}

	async reportDeviceStatus(input: EndpointInput<Endpoint.ReportDeviceStatus>) {
		return this.makeRequestWithThrow(Endpoint.ReportDeviceStatus, input)
	}
	async reportDeviceStatusNoThrow(input: EndpointInput<Endpoint.ReportDeviceStatus>) {
		return this.makeRequestNoThrow(Endpoint.ReportDeviceStatus, input)
	}

	async unenrollCurrentDevice(input: EndpointInput<Endpoint.UnenrollCurrentDevice>) {
		return this.makeRequestWithThrow(Endpoint.UnenrollCurrentDevice, input)
	}
	async unenrollCurrentDeviceNoThrow(input: EndpointInput<Endpoint.UnenrollCurrentDevice>) {
		return this.makeRequestNoThrow(Endpoint.UnenrollCurrentDevice, input)
	}

	async getDevice(input: EndpointInput<Endpoint.GetDevice>) {
		return this.makeRequestWithThrow(Endpoint.GetDevice, input)
	}
	async getDeviceNoThrow(input: EndpointInput<Endpoint.GetDevice>) {
		return this.makeRequestNoThrow(Endpoint.GetDevice, input)
	}

	async updateDevice(input: EndpointInput<Endpoint.UpdateDevice>) {
		return this.makeRequestWithThrow(Endpoint.UpdateDevice, input)
	}
	async updateDeviceNoThrow(input: EndpointInput<Endpoint.UpdateDevice>) {
		return this.makeRequestNoThrow(Endpoint.UpdateDevice, input)
	}

	async deleteDevice(input: EndpointInput<Endpoint.DeleteDevice>) {
		return this.makeRequestWithThrow(Endpoint.DeleteDevice, input)
	}
	async deleteDeviceNoThrow(input: EndpointInput<Endpoint.DeleteDevice>) {
		return this.makeRequestNoThrow(Endpoint.DeleteDevice, input)
	}

	async linkDeviceToPointOfSale(input: EndpointInput<Endpoint.LinkDeviceToPointOfSale>) {
		return this.makeRequestWithThrow(Endpoint.LinkDeviceToPointOfSale, input)
	}
	async linkDeviceToPointOfSaleNoThrow(input: EndpointInput<Endpoint.LinkDeviceToPointOfSale>) {
		return this.makeRequestNoThrow(Endpoint.LinkDeviceToPointOfSale, input)
	}

	async unlinkDeviceFromPointOfSale(input: EndpointInput<Endpoint.UnlinkDeviceFromPointOfSale>) {
		return this.makeRequestWithThrow(Endpoint.UnlinkDeviceFromPointOfSale, input)
	}
	async unlinkDeviceFromPointOfSaleNoThrow(input: EndpointInput<Endpoint.UnlinkDeviceFromPointOfSale>) {
		return this.makeRequestNoThrow(Endpoint.UnlinkDeviceFromPointOfSale, input)
	}

	async createDeviceSecretKey(input: EndpointInput<Endpoint.CreateDeviceSecretKey>) {
		return this.makeRequestWithThrow(Endpoint.CreateDeviceSecretKey, input)
	}
	async createDeviceSecretKeyNoThrow(input: EndpointInput<Endpoint.CreateDeviceSecretKey>) {
		return this.makeRequestNoThrow(Endpoint.CreateDeviceSecretKey, input)
	}

	async getDeviceStatus(input: EndpointInput<Endpoint.GetDeviceStatus>) {
		return this.makeRequestWithThrow(Endpoint.GetDeviceStatus, input)
	}
	async getDeviceStatusNoThrow(input: EndpointInput<Endpoint.GetDeviceStatus>) {
		return this.makeRequestNoThrow(Endpoint.GetDeviceStatus, input)
	}

	async refreshDeviceStatus(input: EndpointInput<Endpoint.RefreshDeviceStatus>) {
		return this.makeRequestWithThrow(Endpoint.RefreshDeviceStatus, input)
	}
	async refreshDeviceStatusNoThrow(input: EndpointInput<Endpoint.RefreshDeviceStatus>) {
		return this.makeRequestNoThrow(Endpoint.RefreshDeviceStatus, input)
	}

	async lookupDeviceEnrollmentByCodeForDevice(input: EndpointInput<Endpoint.LookupDeviceEnrollmentByCodeForDevice>) {
		return this.makeRequestWithThrow(Endpoint.LookupDeviceEnrollmentByCodeForDevice, input)
	}
	async lookupDeviceEnrollmentByCodeForDeviceNoThrow(
		input: EndpointInput<Endpoint.LookupDeviceEnrollmentByCodeForDevice>
	) {
		return this.makeRequestNoThrow(Endpoint.LookupDeviceEnrollmentByCodeForDevice, input)
	}

	async enrollDevice(input: EndpointInput<Endpoint.EnrollDevice>) {
		return this.makeRequestWithThrow(Endpoint.EnrollDevice, input)
	}
	async enrollDeviceNoThrow(input: EndpointInput<Endpoint.EnrollDevice>) {
		return this.makeRequestNoThrow(Endpoint.EnrollDevice, input)
	}

	async unenrollDevice(input: EndpointInput<Endpoint.UnenrollDevice>) {
		return this.makeRequestWithThrow(Endpoint.UnenrollDevice, input)
	}
	async unenrollDeviceNoThrow(input: EndpointInput<Endpoint.UnenrollDevice>) {
		return this.makeRequestNoThrow(Endpoint.UnenrollDevice, input)
	}

	async deleteDeviceGmsDevice(input: EndpointInput<Endpoint.DeleteDeviceGmsDevice>) {
		return this.makeRequestWithThrow(Endpoint.DeleteDeviceGmsDevice, input)
	}
	async deleteDeviceGmsDeviceNoThrow(input: EndpointInput<Endpoint.DeleteDeviceGmsDevice>) {
		return this.makeRequestNoThrow(Endpoint.DeleteDeviceGmsDevice, input)
	}

	async createDeviceGmsEnrollment(input: EndpointInput<Endpoint.CreateDeviceGmsEnrollment>) {
		return this.makeRequestWithThrow(Endpoint.CreateDeviceGmsEnrollment, input)
	}
	async createDeviceGmsEnrollmentNoThrow(input: EndpointInput<Endpoint.CreateDeviceGmsEnrollment>) {
		return this.makeRequestNoThrow(Endpoint.CreateDeviceGmsEnrollment, input)
	}

	async syncDeviceGmsPolicy(input: EndpointInput<Endpoint.SyncDeviceGmsPolicy>) {
		return this.makeRequestWithThrow(Endpoint.SyncDeviceGmsPolicy, input)
	}
	async syncDeviceGmsPolicyNoThrow(input: EndpointInput<Endpoint.SyncDeviceGmsPolicy>) {
		return this.makeRequestNoThrow(Endpoint.SyncDeviceGmsPolicy, input)
	}

	async rebootDeviceGmsDevice(input: EndpointInput<Endpoint.RebootDeviceGmsDevice>) {
		return this.makeRequestWithThrow(Endpoint.RebootDeviceGmsDevice, input)
	}
	async rebootDeviceGmsDeviceNoThrow(input: EndpointInput<Endpoint.RebootDeviceGmsDevice>) {
		return this.makeRequestNoThrow(Endpoint.RebootDeviceGmsDevice, input)
	}

	async listProducts(input: EndpointInput<Endpoint.ListProducts>) {
		return this.makeRequestWithThrow(Endpoint.ListProducts, input)
	}
	async listProductsNoThrow(input: EndpointInput<Endpoint.ListProducts>) {
		return this.makeRequestNoThrow(Endpoint.ListProducts, input)
	}

	async createProduct(input: EndpointInput<Endpoint.CreateProduct>) {
		return this.makeRequestWithThrow(Endpoint.CreateProduct, input)
	}
	async createProductNoThrow(input: EndpointInput<Endpoint.CreateProduct>) {
		return this.makeRequestNoThrow(Endpoint.CreateProduct, input)
	}

	async listProductGroupsForPointOfSale(input: EndpointInput<Endpoint.ListProductGroupsForPointOfSale>) {
		return this.makeRequestWithThrow(Endpoint.ListProductGroupsForPointOfSale, input)
	}
	async listProductGroupsForPointOfSaleNoThrow(input: EndpointInput<Endpoint.ListProductGroupsForPointOfSale>) {
		return this.makeRequestNoThrow(Endpoint.ListProductGroupsForPointOfSale, input)
	}

	async updateProductGroupsForPointOfSale(input: EndpointInput<Endpoint.UpdateProductGroupsForPointOfSale>) {
		return this.makeRequestWithThrow(Endpoint.UpdateProductGroupsForPointOfSale, input)
	}
	async updateProductGroupsForPointOfSaleNoThrow(input: EndpointInput<Endpoint.UpdateProductGroupsForPointOfSale>) {
		return this.makeRequestNoThrow(Endpoint.UpdateProductGroupsForPointOfSale, input)
	}

	async getProductMenu(input: EndpointInput<Endpoint.GetProductMenu>) {
		return this.makeRequestWithThrow(Endpoint.GetProductMenu, input)
	}
	async getProductMenuNoThrow(input: EndpointInput<Endpoint.GetProductMenu>) {
		return this.makeRequestNoThrow(Endpoint.GetProductMenu, input)
	}

	async listAvailableProductsForSalesChannel(input: EndpointInput<Endpoint.ListAvailableProductsForSalesChannel>) {
		return this.makeRequestWithThrow(Endpoint.ListAvailableProductsForSalesChannel, input)
	}
	async listAvailableProductsForSalesChannelNoThrow(
		input: EndpointInput<Endpoint.ListAvailableProductsForSalesChannel>
	) {
		return this.makeRequestNoThrow(Endpoint.ListAvailableProductsForSalesChannel, input)
	}

	async getProductFromBarcode(input: EndpointInput<Endpoint.GetProductFromBarcode>) {
		return this.makeRequestWithThrow(Endpoint.GetProductFromBarcode, input)
	}
	async getProductFromBarcodeNoThrow(input: EndpointInput<Endpoint.GetProductFromBarcode>) {
		return this.makeRequestNoThrow(Endpoint.GetProductFromBarcode, input)
	}

	async listProductGroups(input: EndpointInput<Endpoint.ListProductGroups>) {
		return this.makeRequestWithThrow(Endpoint.ListProductGroups, input)
	}
	async listProductGroupsNoThrow(input: EndpointInput<Endpoint.ListProductGroups>) {
		return this.makeRequestNoThrow(Endpoint.ListProductGroups, input)
	}

	async createProductGroup(input: EndpointInput<Endpoint.CreateProductGroup>) {
		return this.makeRequestWithThrow(Endpoint.CreateProductGroup, input)
	}
	async createProductGroupNoThrow(input: EndpointInput<Endpoint.CreateProductGroup>) {
		return this.makeRequestNoThrow(Endpoint.CreateProductGroup, input)
	}

	async getProductGroup(input: EndpointInput<Endpoint.GetProductGroup>) {
		return this.makeRequestWithThrow(Endpoint.GetProductGroup, input)
	}
	async getProductGroupNoThrow(input: EndpointInput<Endpoint.GetProductGroup>) {
		return this.makeRequestNoThrow(Endpoint.GetProductGroup, input)
	}

	async deleteProductGroup(input: EndpointInput<Endpoint.DeleteProductGroup>) {
		return this.makeRequestWithThrow(Endpoint.DeleteProductGroup, input)
	}
	async deleteProductGroupNoThrow(input: EndpointInput<Endpoint.DeleteProductGroup>) {
		return this.makeRequestNoThrow(Endpoint.DeleteProductGroup, input)
	}

	async updateProductGroup(input: EndpointInput<Endpoint.UpdateProductGroup>) {
		return this.makeRequestWithThrow(Endpoint.UpdateProductGroup, input)
	}
	async updateProductGroupNoThrow(input: EndpointInput<Endpoint.UpdateProductGroup>) {
		return this.makeRequestNoThrow(Endpoint.UpdateProductGroup, input)
	}

	async setProductGroupImage(input: EndpointInput<Endpoint.SetProductGroupImage>) {
		return this.makeRequestWithThrow(Endpoint.SetProductGroupImage, input)
	}
	async setProductGroupImageNoThrow(input: EndpointInput<Endpoint.SetProductGroupImage>) {
		return this.makeRequestNoThrow(Endpoint.SetProductGroupImage, input)
	}

	async deleteProductGroupImage(input: EndpointInput<Endpoint.DeleteProductGroupImage>) {
		return this.makeRequestWithThrow(Endpoint.DeleteProductGroupImage, input)
	}
	async deleteProductGroupImageNoThrow(input: EndpointInput<Endpoint.DeleteProductGroupImage>) {
		return this.makeRequestNoThrow(Endpoint.DeleteProductGroupImage, input)
	}

	async addProductsToProductGroup(input: EndpointInput<Endpoint.AddProductsToProductGroup>) {
		return this.makeRequestWithThrow(Endpoint.AddProductsToProductGroup, input)
	}
	async addProductsToProductGroupNoThrow(input: EndpointInput<Endpoint.AddProductsToProductGroup>) {
		return this.makeRequestNoThrow(Endpoint.AddProductsToProductGroup, input)
	}

	async listProductGroupFees(input: EndpointInput<Endpoint.ListProductGroupFees>) {
		return this.makeRequestWithThrow(Endpoint.ListProductGroupFees, input)
	}
	async listProductGroupFeesNoThrow(input: EndpointInput<Endpoint.ListProductGroupFees>) {
		return this.makeRequestNoThrow(Endpoint.ListProductGroupFees, input)
	}

	async createProductGroupFee(input: EndpointInput<Endpoint.CreateProductGroupFee>) {
		return this.makeRequestWithThrow(Endpoint.CreateProductGroupFee, input)
	}
	async createProductGroupFeeNoThrow(input: EndpointInput<Endpoint.CreateProductGroupFee>) {
		return this.makeRequestNoThrow(Endpoint.CreateProductGroupFee, input)
	}

	async getProductGroupFee(input: EndpointInput<Endpoint.GetProductGroupFee>) {
		return this.makeRequestWithThrow(Endpoint.GetProductGroupFee, input)
	}
	async getProductGroupFeeNoThrow(input: EndpointInput<Endpoint.GetProductGroupFee>) {
		return this.makeRequestNoThrow(Endpoint.GetProductGroupFee, input)
	}

	async deleteProductGroupFee(input: EndpointInput<Endpoint.DeleteProductGroupFee>) {
		return this.makeRequestWithThrow(Endpoint.DeleteProductGroupFee, input)
	}
	async deleteProductGroupFeeNoThrow(input: EndpointInput<Endpoint.DeleteProductGroupFee>) {
		return this.makeRequestNoThrow(Endpoint.DeleteProductGroupFee, input)
	}

	async updateProductGroupFee(input: EndpointInput<Endpoint.UpdateProductGroupFee>) {
		return this.makeRequestWithThrow(Endpoint.UpdateProductGroupFee, input)
	}
	async updateProductGroupFeeNoThrow(input: EndpointInput<Endpoint.UpdateProductGroupFee>) {
		return this.makeRequestNoThrow(Endpoint.UpdateProductGroupFee, input)
	}

	async listProductCategories(input: EndpointInput<Endpoint.ListProductCategories>) {
		return this.makeRequestWithThrow(Endpoint.ListProductCategories, input)
	}
	async listProductCategoriesNoThrow(input: EndpointInput<Endpoint.ListProductCategories>) {
		return this.makeRequestNoThrow(Endpoint.ListProductCategories, input)
	}

	async createProductCategory(input: EndpointInput<Endpoint.CreateProductCategory>) {
		return this.makeRequestWithThrow(Endpoint.CreateProductCategory, input)
	}
	async createProductCategoryNoThrow(input: EndpointInput<Endpoint.CreateProductCategory>) {
		return this.makeRequestNoThrow(Endpoint.CreateProductCategory, input)
	}

	async getProductCategoryHierarchy(input: EndpointInput<Endpoint.GetProductCategoryHierarchy>) {
		return this.makeRequestWithThrow(Endpoint.GetProductCategoryHierarchy, input)
	}
	async getProductCategoryHierarchyNoThrow(input: EndpointInput<Endpoint.GetProductCategoryHierarchy>) {
		return this.makeRequestNoThrow(Endpoint.GetProductCategoryHierarchy, input)
	}

	async getProductCategory(input: EndpointInput<Endpoint.GetProductCategory>) {
		return this.makeRequestWithThrow(Endpoint.GetProductCategory, input)
	}
	async getProductCategoryNoThrow(input: EndpointInput<Endpoint.GetProductCategory>) {
		return this.makeRequestNoThrow(Endpoint.GetProductCategory, input)
	}

	async deleteProductCategory(input: EndpointInput<Endpoint.DeleteProductCategory>) {
		return this.makeRequestWithThrow(Endpoint.DeleteProductCategory, input)
	}
	async deleteProductCategoryNoThrow(input: EndpointInput<Endpoint.DeleteProductCategory>) {
		return this.makeRequestNoThrow(Endpoint.DeleteProductCategory, input)
	}

	async updateProductCategory(input: EndpointInput<Endpoint.UpdateProductCategory>) {
		return this.makeRequestWithThrow(Endpoint.UpdateProductCategory, input)
	}
	async updateProductCategoryNoThrow(input: EndpointInput<Endpoint.UpdateProductCategory>) {
		return this.makeRequestNoThrow(Endpoint.UpdateProductCategory, input)
	}

	async setProductCategoryImage(input: EndpointInput<Endpoint.SetProductCategoryImage>) {
		return this.makeRequestWithThrow(Endpoint.SetProductCategoryImage, input)
	}
	async setProductCategoryImageNoThrow(input: EndpointInput<Endpoint.SetProductCategoryImage>) {
		return this.makeRequestNoThrow(Endpoint.SetProductCategoryImage, input)
	}

	async deleteProductCategoryImage(input: EndpointInput<Endpoint.DeleteProductCategoryImage>) {
		return this.makeRequestWithThrow(Endpoint.DeleteProductCategoryImage, input)
	}
	async deleteProductCategoryImageNoThrow(input: EndpointInput<Endpoint.DeleteProductCategoryImage>) {
		return this.makeRequestNoThrow(Endpoint.DeleteProductCategoryImage, input)
	}

	async getProduct(input: EndpointInput<Endpoint.GetProduct>) {
		return this.makeRequestWithThrow(Endpoint.GetProduct, input)
	}
	async getProductNoThrow(input: EndpointInput<Endpoint.GetProduct>) {
		return this.makeRequestNoThrow(Endpoint.GetProduct, input)
	}

	async deleteProduct(input: EndpointInput<Endpoint.DeleteProduct>) {
		return this.makeRequestWithThrow(Endpoint.DeleteProduct, input)
	}
	async deleteProductNoThrow(input: EndpointInput<Endpoint.DeleteProduct>) {
		return this.makeRequestNoThrow(Endpoint.DeleteProduct, input)
	}

	async updateProduct(input: EndpointInput<Endpoint.UpdateProduct>) {
		return this.makeRequestWithThrow(Endpoint.UpdateProduct, input)
	}
	async updateProductNoThrow(input: EndpointInput<Endpoint.UpdateProduct>) {
		return this.makeRequestNoThrow(Endpoint.UpdateProduct, input)
	}

	async getProductEdit(input: EndpointInput<Endpoint.GetProductEdit>) {
		return this.makeRequestWithThrow(Endpoint.GetProductEdit, input)
	}
	async getProductEditNoThrow(input: EndpointInput<Endpoint.GetProductEdit>) {
		return this.makeRequestNoThrow(Endpoint.GetProductEdit, input)
	}

	async listProductBarcodes(input: EndpointInput<Endpoint.ListProductBarcodes>) {
		return this.makeRequestWithThrow(Endpoint.ListProductBarcodes, input)
	}
	async listProductBarcodesNoThrow(input: EndpointInput<Endpoint.ListProductBarcodes>) {
		return this.makeRequestNoThrow(Endpoint.ListProductBarcodes, input)
	}

	async createProductBarcode(input: EndpointInput<Endpoint.CreateProductBarcode>) {
		return this.makeRequestWithThrow(Endpoint.CreateProductBarcode, input)
	}
	async createProductBarcodeNoThrow(input: EndpointInput<Endpoint.CreateProductBarcode>) {
		return this.makeRequestNoThrow(Endpoint.CreateProductBarcode, input)
	}

	async deleteProductBarcode(input: EndpointInput<Endpoint.DeleteProductBarcode>) {
		return this.makeRequestWithThrow(Endpoint.DeleteProductBarcode, input)
	}
	async deleteProductBarcodeNoThrow(input: EndpointInput<Endpoint.DeleteProductBarcode>) {
		return this.makeRequestNoThrow(Endpoint.DeleteProductBarcode, input)
	}

	async createProductUnitOfMeasure(input: EndpointInput<Endpoint.CreateProductUnitOfMeasure>) {
		return this.makeRequestWithThrow(Endpoint.CreateProductUnitOfMeasure, input)
	}
	async createProductUnitOfMeasureNoThrow(input: EndpointInput<Endpoint.CreateProductUnitOfMeasure>) {
		return this.makeRequestNoThrow(Endpoint.CreateProductUnitOfMeasure, input)
	}

	async setProductBaseUnit(input: EndpointInput<Endpoint.SetProductBaseUnit>) {
		return this.makeRequestWithThrow(Endpoint.SetProductBaseUnit, input)
	}
	async setProductBaseUnitNoThrow(input: EndpointInput<Endpoint.SetProductBaseUnit>) {
		return this.makeRequestNoThrow(Endpoint.SetProductBaseUnit, input)
	}

	async updateProductUnitOfMeasure(input: EndpointInput<Endpoint.UpdateProductUnitOfMeasure>) {
		return this.makeRequestWithThrow(Endpoint.UpdateProductUnitOfMeasure, input)
	}
	async updateProductUnitOfMeasureNoThrow(input: EndpointInput<Endpoint.UpdateProductUnitOfMeasure>) {
		return this.makeRequestNoThrow(Endpoint.UpdateProductUnitOfMeasure, input)
	}

	async deleteProductUnitOfMeasure(input: EndpointInput<Endpoint.DeleteProductUnitOfMeasure>) {
		return this.makeRequestWithThrow(Endpoint.DeleteProductUnitOfMeasure, input)
	}
	async deleteProductUnitOfMeasureNoThrow(input: EndpointInput<Endpoint.DeleteProductUnitOfMeasure>) {
		return this.makeRequestNoThrow(Endpoint.DeleteProductUnitOfMeasure, input)
	}

	async getProductPricing(input: EndpointInput<Endpoint.GetProductPricing>) {
		return this.makeRequestWithThrow(Endpoint.GetProductPricing, input)
	}
	async getProductPricingNoThrow(input: EndpointInput<Endpoint.GetProductPricing>) {
		return this.makeRequestNoThrow(Endpoint.GetProductPricing, input)
	}

	async updateProductPricing(input: EndpointInput<Endpoint.UpdateProductPricing>) {
		return this.makeRequestWithThrow(Endpoint.UpdateProductPricing, input)
	}
	async updateProductPricingNoThrow(input: EndpointInput<Endpoint.UpdateProductPricing>) {
		return this.makeRequestNoThrow(Endpoint.UpdateProductPricing, input)
	}

	async getProductOrganization(input: EndpointInput<Endpoint.GetProductOrganization>) {
		return this.makeRequestWithThrow(Endpoint.GetProductOrganization, input)
	}
	async getProductOrganizationNoThrow(input: EndpointInput<Endpoint.GetProductOrganization>) {
		return this.makeRequestNoThrow(Endpoint.GetProductOrganization, input)
	}

	async updateProductOrganization(input: EndpointInput<Endpoint.UpdateProductOrganization>) {
		return this.makeRequestWithThrow(Endpoint.UpdateProductOrganization, input)
	}
	async updateProductOrganizationNoThrow(input: EndpointInput<Endpoint.UpdateProductOrganization>) {
		return this.makeRequestNoThrow(Endpoint.UpdateProductOrganization, input)
	}

	async listProductImages(input: EndpointInput<Endpoint.ListProductImages>) {
		return this.makeRequestWithThrow(Endpoint.ListProductImages, input)
	}
	async listProductImagesNoThrow(input: EndpointInput<Endpoint.ListProductImages>) {
		return this.makeRequestNoThrow(Endpoint.ListProductImages, input)
	}

	async attachProductImage(input: EndpointInput<Endpoint.AttachProductImage>) {
		return this.makeRequestWithThrow(Endpoint.AttachProductImage, input)
	}
	async attachProductImageNoThrow(input: EndpointInput<Endpoint.AttachProductImage>) {
		return this.makeRequestNoThrow(Endpoint.AttachProductImage, input)
	}

	async reorderProductImages(input: EndpointInput<Endpoint.ReorderProductImages>) {
		return this.makeRequestWithThrow(Endpoint.ReorderProductImages, input)
	}
	async reorderProductImagesNoThrow(input: EndpointInput<Endpoint.ReorderProductImages>) {
		return this.makeRequestNoThrow(Endpoint.ReorderProductImages, input)
	}

	async deleteProductImage(input: EndpointInput<Endpoint.DeleteProductImage>) {
		return this.makeRequestWithThrow(Endpoint.DeleteProductImage, input)
	}
	async deleteProductImageNoThrow(input: EndpointInput<Endpoint.DeleteProductImage>) {
		return this.makeRequestNoThrow(Endpoint.DeleteProductImage, input)
	}

	async setPrimaryProductImage(input: EndpointInput<Endpoint.SetPrimaryProductImage>) {
		return this.makeRequestWithThrow(Endpoint.SetPrimaryProductImage, input)
	}
	async setPrimaryProductImageNoThrow(input: EndpointInput<Endpoint.SetPrimaryProductImage>) {
		return this.makeRequestNoThrow(Endpoint.SetPrimaryProductImage, input)
	}

	async listProductVariants(input: EndpointInput<Endpoint.ListProductVariants>) {
		return this.makeRequestWithThrow(Endpoint.ListProductVariants, input)
	}
	async listProductVariantsNoThrow(input: EndpointInput<Endpoint.ListProductVariants>) {
		return this.makeRequestNoThrow(Endpoint.ListProductVariants, input)
	}

	async createProductVariant(input: EndpointInput<Endpoint.CreateProductVariant>) {
		return this.makeRequestWithThrow(Endpoint.CreateProductVariant, input)
	}
	async createProductVariantNoThrow(input: EndpointInput<Endpoint.CreateProductVariant>) {
		return this.makeRequestNoThrow(Endpoint.CreateProductVariant, input)
	}

	async getProductVariant(input: EndpointInput<Endpoint.GetProductVariant>) {
		return this.makeRequestWithThrow(Endpoint.GetProductVariant, input)
	}
	async getProductVariantNoThrow(input: EndpointInput<Endpoint.GetProductVariant>) {
		return this.makeRequestNoThrow(Endpoint.GetProductVariant, input)
	}

	async updateProductVariant(input: EndpointInput<Endpoint.UpdateProductVariant>) {
		return this.makeRequestWithThrow(Endpoint.UpdateProductVariant, input)
	}
	async updateProductVariantNoThrow(input: EndpointInput<Endpoint.UpdateProductVariant>) {
		return this.makeRequestNoThrow(Endpoint.UpdateProductVariant, input)
	}

	async createVariantBarcode(input: EndpointInput<Endpoint.CreateVariantBarcode>) {
		return this.makeRequestWithThrow(Endpoint.CreateVariantBarcode, input)
	}
	async createVariantBarcodeNoThrow(input: EndpointInput<Endpoint.CreateVariantBarcode>) {
		return this.makeRequestNoThrow(Endpoint.CreateVariantBarcode, input)
	}

	async listVariantBarcodes(input: EndpointInput<Endpoint.ListVariantBarcodes>) {
		return this.makeRequestWithThrow(Endpoint.ListVariantBarcodes, input)
	}
	async listVariantBarcodesNoThrow(input: EndpointInput<Endpoint.ListVariantBarcodes>) {
		return this.makeRequestNoThrow(Endpoint.ListVariantBarcodes, input)
	}

	async deleteVariantBarcode(input: EndpointInput<Endpoint.DeleteVariantBarcode>) {
		return this.makeRequestWithThrow(Endpoint.DeleteVariantBarcode, input)
	}
	async deleteVariantBarcodeNoThrow(input: EndpointInput<Endpoint.DeleteVariantBarcode>) {
		return this.makeRequestNoThrow(Endpoint.DeleteVariantBarcode, input)
	}

	async getVariantPricing(input: EndpointInput<Endpoint.GetVariantPricing>) {
		return this.makeRequestWithThrow(Endpoint.GetVariantPricing, input)
	}
	async getVariantPricingNoThrow(input: EndpointInput<Endpoint.GetVariantPricing>) {
		return this.makeRequestNoThrow(Endpoint.GetVariantPricing, input)
	}

	async updateVariantPricing(input: EndpointInput<Endpoint.UpdateVariantPricing>) {
		return this.makeRequestWithThrow(Endpoint.UpdateVariantPricing, input)
	}
	async updateVariantPricingNoThrow(input: EndpointInput<Endpoint.UpdateVariantPricing>) {
		return this.makeRequestNoThrow(Endpoint.UpdateVariantPricing, input)
	}

	async listUnitsOfMeasure(input: EndpointInput<Endpoint.ListUnitsOfMeasure>) {
		return this.makeRequestWithThrow(Endpoint.ListUnitsOfMeasure, input)
	}
	async listUnitsOfMeasureNoThrow(input: EndpointInput<Endpoint.ListUnitsOfMeasure>) {
		return this.makeRequestNoThrow(Endpoint.ListUnitsOfMeasure, input)
	}

	async createUnitOfMeasure(input: EndpointInput<Endpoint.CreateUnitOfMeasure>) {
		return this.makeRequestWithThrow(Endpoint.CreateUnitOfMeasure, input)
	}
	async createUnitOfMeasureNoThrow(input: EndpointInput<Endpoint.CreateUnitOfMeasure>) {
		return this.makeRequestNoThrow(Endpoint.CreateUnitOfMeasure, input)
	}

	async updateUnitOfMeasure(input: EndpointInput<Endpoint.UpdateUnitOfMeasure>) {
		return this.makeRequestWithThrow(Endpoint.UpdateUnitOfMeasure, input)
	}
	async updateUnitOfMeasureNoThrow(input: EndpointInput<Endpoint.UpdateUnitOfMeasure>) {
		return this.makeRequestNoThrow(Endpoint.UpdateUnitOfMeasure, input)
	}

	async deleteUnitOfMeasure(input: EndpointInput<Endpoint.DeleteUnitOfMeasure>) {
		return this.makeRequestWithThrow(Endpoint.DeleteUnitOfMeasure, input)
	}
	async deleteUnitOfMeasureNoThrow(input: EndpointInput<Endpoint.DeleteUnitOfMeasure>) {
		return this.makeRequestNoThrow(Endpoint.DeleteUnitOfMeasure, input)
	}

	async exportProducts(input: EndpointInput<Endpoint.ExportProducts>) {
		return this.makeRequestWithThrow(Endpoint.ExportProducts, input)
	}
	async exportProductsNoThrow(input: EndpointInput<Endpoint.ExportProducts>) {
		return this.makeRequestNoThrow(Endpoint.ExportProducts, input)
	}

	async importProducts(input: EndpointInput<Endpoint.ImportProducts>) {
		return this.makeRequestWithThrow(Endpoint.ImportProducts, input)
	}
	async importProductsNoThrow(input: EndpointInput<Endpoint.ImportProducts>) {
		return this.makeRequestNoThrow(Endpoint.ImportProducts, input)
	}

	async validateProductImport(input: EndpointInput<Endpoint.ValidateProductImport>) {
		return this.makeRequestWithThrow(Endpoint.ValidateProductImport, input)
	}
	async validateProductImportNoThrow(input: EndpointInput<Endpoint.ValidateProductImport>) {
		return this.makeRequestNoThrow(Endpoint.ValidateProductImport, input)
	}

	async listAssets(input: EndpointInput<Endpoint.ListAssets>) {
		return this.makeRequestWithThrow(Endpoint.ListAssets, input)
	}
	async listAssetsNoThrow(input: EndpointInput<Endpoint.ListAssets>) {
		return this.makeRequestNoThrow(Endpoint.ListAssets, input)
	}

	async initAssetUpload(input: EndpointInput<Endpoint.InitAssetUpload>) {
		return this.makeRequestWithThrow(Endpoint.InitAssetUpload, input)
	}
	async initAssetUploadNoThrow(input: EndpointInput<Endpoint.InitAssetUpload>) {
		return this.makeRequestNoThrow(Endpoint.InitAssetUpload, input)
	}

	async listAssetTags(input: EndpointInput<Endpoint.ListAssetTags>) {
		return this.makeRequestWithThrow(Endpoint.ListAssetTags, input)
	}
	async listAssetTagsNoThrow(input: EndpointInput<Endpoint.ListAssetTags>) {
		return this.makeRequestNoThrow(Endpoint.ListAssetTags, input)
	}

	async createAssetTag(input: EndpointInput<Endpoint.CreateAssetTag>) {
		return this.makeRequestWithThrow(Endpoint.CreateAssetTag, input)
	}
	async createAssetTagNoThrow(input: EndpointInput<Endpoint.CreateAssetTag>) {
		return this.makeRequestNoThrow(Endpoint.CreateAssetTag, input)
	}

	async removeTagFromAssets(input: EndpointInput<Endpoint.RemoveTagFromAssets>) {
		return this.makeRequestWithThrow(Endpoint.RemoveTagFromAssets, input)
	}
	async removeTagFromAssetsNoThrow(input: EndpointInput<Endpoint.RemoveTagFromAssets>) {
		return this.makeRequestNoThrow(Endpoint.RemoveTagFromAssets, input)
	}

	async renameAssetTag(input: EndpointInput<Endpoint.RenameAssetTag>) {
		return this.makeRequestWithThrow(Endpoint.RenameAssetTag, input)
	}
	async renameAssetTagNoThrow(input: EndpointInput<Endpoint.RenameAssetTag>) {
		return this.makeRequestNoThrow(Endpoint.RenameAssetTag, input)
	}

	async getAsset(input: EndpointInput<Endpoint.GetAsset>) {
		return this.makeRequestWithThrow(Endpoint.GetAsset, input)
	}
	async getAssetNoThrow(input: EndpointInput<Endpoint.GetAsset>) {
		return this.makeRequestNoThrow(Endpoint.GetAsset, input)
	}

	async updateAsset(input: EndpointInput<Endpoint.UpdateAsset>) {
		return this.makeRequestWithThrow(Endpoint.UpdateAsset, input)
	}
	async updateAssetNoThrow(input: EndpointInput<Endpoint.UpdateAsset>) {
		return this.makeRequestNoThrow(Endpoint.UpdateAsset, input)
	}

	async deleteAsset(input: EndpointInput<Endpoint.DeleteAsset>) {
		return this.makeRequestWithThrow(Endpoint.DeleteAsset, input)
	}
	async deleteAssetNoThrow(input: EndpointInput<Endpoint.DeleteAsset>) {
		return this.makeRequestNoThrow(Endpoint.DeleteAsset, input)
	}

	async completeAssetUpload(input: EndpointInput<Endpoint.CompleteAssetUpload>) {
		return this.makeRequestWithThrow(Endpoint.CompleteAssetUpload, input)
	}
	async completeAssetUploadNoThrow(input: EndpointInput<Endpoint.CompleteAssetUpload>) {
		return this.makeRequestNoThrow(Endpoint.CompleteAssetUpload, input)
	}

	async listMarketingChannels(input: EndpointInput<Endpoint.ListMarketingChannels>) {
		return this.makeRequestWithThrow(Endpoint.ListMarketingChannels, input)
	}
	async listMarketingChannelsNoThrow(input: EndpointInput<Endpoint.ListMarketingChannels>) {
		return this.makeRequestNoThrow(Endpoint.ListMarketingChannels, input)
	}

	async createMarketingChannel(input: EndpointInput<Endpoint.CreateMarketingChannel>) {
		return this.makeRequestWithThrow(Endpoint.CreateMarketingChannel, input)
	}
	async createMarketingChannelNoThrow(input: EndpointInput<Endpoint.CreateMarketingChannel>) {
		return this.makeRequestNoThrow(Endpoint.CreateMarketingChannel, input)
	}

	async getMarketingChannel(input: EndpointInput<Endpoint.GetMarketingChannel>) {
		return this.makeRequestWithThrow(Endpoint.GetMarketingChannel, input)
	}
	async getMarketingChannelNoThrow(input: EndpointInput<Endpoint.GetMarketingChannel>) {
		return this.makeRequestNoThrow(Endpoint.GetMarketingChannel, input)
	}

	async updateMarketingChannel(input: EndpointInput<Endpoint.UpdateMarketingChannel>) {
		return this.makeRequestWithThrow(Endpoint.UpdateMarketingChannel, input)
	}
	async updateMarketingChannelNoThrow(input: EndpointInput<Endpoint.UpdateMarketingChannel>) {
		return this.makeRequestNoThrow(Endpoint.UpdateMarketingChannel, input)
	}

	async deleteMarketingChannel(input: EndpointInput<Endpoint.DeleteMarketingChannel>) {
		return this.makeRequestWithThrow(Endpoint.DeleteMarketingChannel, input)
	}
	async deleteMarketingChannelNoThrow(input: EndpointInput<Endpoint.DeleteMarketingChannel>) {
		return this.makeRequestNoThrow(Endpoint.DeleteMarketingChannel, input)
	}

	async getFeedLayout(input: EndpointInput<Endpoint.GetFeedLayout>) {
		return this.makeRequestWithThrow(Endpoint.GetFeedLayout, input)
	}
	async getFeedLayoutNoThrow(input: EndpointInput<Endpoint.GetFeedLayout>) {
		return this.makeRequestNoThrow(Endpoint.GetFeedLayout, input)
	}

	async replaceFeedLayoutBlocks(input: EndpointInput<Endpoint.ReplaceFeedLayoutBlocks>) {
		return this.makeRequestWithThrow(Endpoint.ReplaceFeedLayoutBlocks, input)
	}
	async replaceFeedLayoutBlocksNoThrow(input: EndpointInput<Endpoint.ReplaceFeedLayoutBlocks>) {
		return this.makeRequestNoThrow(Endpoint.ReplaceFeedLayoutBlocks, input)
	}

	async getChannelLayout(input: EndpointInput<Endpoint.GetChannelLayout>) {
		return this.makeRequestWithThrow(Endpoint.GetChannelLayout, input)
	}
	async getChannelLayoutNoThrow(input: EndpointInput<Endpoint.GetChannelLayout>) {
		return this.makeRequestNoThrow(Endpoint.GetChannelLayout, input)
	}

	async replaceChannelLayout(input: EndpointInput<Endpoint.ReplaceChannelLayout>) {
		return this.makeRequestWithThrow(Endpoint.ReplaceChannelLayout, input)
	}
	async replaceChannelLayoutNoThrow(input: EndpointInput<Endpoint.ReplaceChannelLayout>) {
		return this.makeRequestNoThrow(Endpoint.ReplaceChannelLayout, input)
	}

	async getChannelResolvedContent(input: EndpointInput<Endpoint.GetChannelResolvedContent>) {
		return this.makeRequestWithThrow(Endpoint.GetChannelResolvedContent, input)
	}
	async getChannelResolvedContentNoThrow(input: EndpointInput<Endpoint.GetChannelResolvedContent>) {
		return this.makeRequestNoThrow(Endpoint.GetChannelResolvedContent, input)
	}

	async listMarketingContent(input: EndpointInput<Endpoint.ListMarketingContent>) {
		return this.makeRequestWithThrow(Endpoint.ListMarketingContent, input)
	}
	async listMarketingContentNoThrow(input: EndpointInput<Endpoint.ListMarketingContent>) {
		return this.makeRequestNoThrow(Endpoint.ListMarketingContent, input)
	}

	async createMarketingContent(input: EndpointInput<Endpoint.CreateMarketingContent>) {
		return this.makeRequestWithThrow(Endpoint.CreateMarketingContent, input)
	}
	async createMarketingContentNoThrow(input: EndpointInput<Endpoint.CreateMarketingContent>) {
		return this.makeRequestNoThrow(Endpoint.CreateMarketingContent, input)
	}

	async getMarketingContent(input: EndpointInput<Endpoint.GetMarketingContent>) {
		return this.makeRequestWithThrow(Endpoint.GetMarketingContent, input)
	}
	async getMarketingContentNoThrow(input: EndpointInput<Endpoint.GetMarketingContent>) {
		return this.makeRequestNoThrow(Endpoint.GetMarketingContent, input)
	}

	async updateMarketingContent(input: EndpointInput<Endpoint.UpdateMarketingContent>) {
		return this.makeRequestWithThrow(Endpoint.UpdateMarketingContent, input)
	}
	async updateMarketingContentNoThrow(input: EndpointInput<Endpoint.UpdateMarketingContent>) {
		return this.makeRequestNoThrow(Endpoint.UpdateMarketingContent, input)
	}

	async deleteMarketingContent(input: EndpointInput<Endpoint.DeleteMarketingContent>) {
		return this.makeRequestWithThrow(Endpoint.DeleteMarketingContent, input)
	}
	async deleteMarketingContentNoThrow(input: EndpointInput<Endpoint.DeleteMarketingContent>) {
		return this.makeRequestNoThrow(Endpoint.DeleteMarketingContent, input)
	}

	async replaceMarketingContentBlocks(input: EndpointInput<Endpoint.ReplaceMarketingContentBlocks>) {
		return this.makeRequestWithThrow(Endpoint.ReplaceMarketingContentBlocks, input)
	}
	async replaceMarketingContentBlocksNoThrow(input: EndpointInput<Endpoint.ReplaceMarketingContentBlocks>) {
		return this.makeRequestNoThrow(Endpoint.ReplaceMarketingContentBlocks, input)
	}

	async duplicateMarketingContent(input: EndpointInput<Endpoint.DuplicateMarketingContent>) {
		return this.makeRequestWithThrow(Endpoint.DuplicateMarketingContent, input)
	}
	async duplicateMarketingContentNoThrow(input: EndpointInput<Endpoint.DuplicateMarketingContent>) {
		return this.makeRequestNoThrow(Endpoint.DuplicateMarketingContent, input)
	}

	async listOrders(input: EndpointInput<Endpoint.ListOrders>) {
		return this.makeRequestWithThrow(Endpoint.ListOrders, input)
	}
	async listOrdersNoThrow(input: EndpointInput<Endpoint.ListOrders>) {
		return this.makeRequestNoThrow(Endpoint.ListOrders, input)
	}

	async getOrder(input: EndpointInput<Endpoint.GetOrder>) {
		return this.makeRequestWithThrow(Endpoint.GetOrder, input)
	}
	async getOrderNoThrow(input: EndpointInput<Endpoint.GetOrder>) {
		return this.makeRequestNoThrow(Endpoint.GetOrder, input)
	}

	async refundOrder(input: EndpointInput<Endpoint.RefundOrder>) {
		return this.makeRequestWithThrow(Endpoint.RefundOrder, input)
	}
	async refundOrderNoThrow(input: EndpointInput<Endpoint.RefundOrder>) {
		return this.makeRequestNoThrow(Endpoint.RefundOrder, input)
	}

	async cancelOrder(input: EndpointInput<Endpoint.CancelOrder>) {
		return this.makeRequestWithThrow(Endpoint.CancelOrder, input)
	}
	async cancelOrderNoThrow(input: EndpointInput<Endpoint.CancelOrder>) {
		return this.makeRequestNoThrow(Endpoint.CancelOrder, input)
	}

	async getExitCode(input: EndpointInput<Endpoint.GetExitCode>) {
		return this.makeRequestWithThrow(Endpoint.GetExitCode, input)
	}
	async getExitCodeNoThrow(input: EndpointInput<Endpoint.GetExitCode>) {
		return this.makeRequestNoThrow(Endpoint.GetExitCode, input)
	}

	async getOrderReceipt(input: EndpointInput<Endpoint.GetOrderReceipt>) {
		return this.makeRequestWithThrow(Endpoint.GetOrderReceipt, input)
	}
	async getOrderReceiptNoThrow(input: EndpointInput<Endpoint.GetOrderReceipt>) {
		return this.makeRequestNoThrow(Endpoint.GetOrderReceipt, input)
	}

	async getOrderReceiptUrl(input: EndpointInput<Endpoint.GetOrderReceiptUrl>) {
		return this.makeRequestWithThrow(Endpoint.GetOrderReceiptUrl, input)
	}
	async getOrderReceiptUrlNoThrow(input: EndpointInput<Endpoint.GetOrderReceiptUrl>) {
		return this.makeRequestNoThrow(Endpoint.GetOrderReceiptUrl, input)
	}

	async invalidateOrderReceipt(input: EndpointInput<Endpoint.InvalidateOrderReceipt>) {
		return this.makeRequestWithThrow(Endpoint.InvalidateOrderReceipt, input)
	}
	async invalidateOrderReceiptNoThrow(input: EndpointInput<Endpoint.InvalidateOrderReceipt>) {
		return this.makeRequestNoThrow(Endpoint.InvalidateOrderReceipt, input)
	}

	async createOrderReceiptLink(input: EndpointInput<Endpoint.CreateOrderReceiptLink>) {
		return this.makeRequestWithThrow(Endpoint.CreateOrderReceiptLink, input)
	}
	async createOrderReceiptLinkNoThrow(input: EndpointInput<Endpoint.CreateOrderReceiptLink>) {
		return this.makeRequestNoThrow(Endpoint.CreateOrderReceiptLink, input)
	}

	async updateFulfillmentStatus(input: EndpointInput<Endpoint.UpdateFulfillmentStatus>) {
		return this.makeRequestWithThrow(Endpoint.UpdateFulfillmentStatus, input)
	}
	async updateFulfillmentStatusNoThrow(input: EndpointInput<Endpoint.UpdateFulfillmentStatus>) {
		return this.makeRequestNoThrow(Endpoint.UpdateFulfillmentStatus, input)
	}

	async getOrderTransaction(input: EndpointInput<Endpoint.GetOrderTransaction>) {
		return this.makeRequestWithThrow(Endpoint.GetOrderTransaction, input)
	}
	async getOrderTransactionNoThrow(input: EndpointInput<Endpoint.GetOrderTransaction>) {
		return this.makeRequestNoThrow(Endpoint.GetOrderTransaction, input)
	}

	async exportOrders(input: EndpointInput<Endpoint.ExportOrders>) {
		return this.makeRequestWithThrow(Endpoint.ExportOrders, input)
	}
	async exportOrdersNoThrow(input: EndpointInput<Endpoint.ExportOrders>) {
		return this.makeRequestNoThrow(Endpoint.ExportOrders, input)
	}

	async createBagCheck(input: EndpointInput<Endpoint.CreateBagCheck>) {
		return this.makeRequestWithThrow(Endpoint.CreateBagCheck, input)
	}
	async createBagCheckNoThrow(input: EndpointInput<Endpoint.CreateBagCheck>) {
		return this.makeRequestNoThrow(Endpoint.CreateBagCheck, input)
	}

	async getBagCheck(input: EndpointInput<Endpoint.GetBagCheck>) {
		return this.makeRequestWithThrow(Endpoint.GetBagCheck, input)
	}
	async getBagCheckNoThrow(input: EndpointInput<Endpoint.GetBagCheck>) {
		return this.makeRequestNoThrow(Endpoint.GetBagCheck, input)
	}

	async bagCheckAddItem(input: EndpointInput<Endpoint.BagCheckAddItem>) {
		return this.makeRequestWithThrow(Endpoint.BagCheckAddItem, input)
	}
	async bagCheckAddItemNoThrow(input: EndpointInput<Endpoint.BagCheckAddItem>) {
		return this.makeRequestNoThrow(Endpoint.BagCheckAddItem, input)
	}

	async bagCheckUpdateItemQuantity(input: EndpointInput<Endpoint.BagCheckUpdateItemQuantity>) {
		return this.makeRequestWithThrow(Endpoint.BagCheckUpdateItemQuantity, input)
	}
	async bagCheckUpdateItemQuantityNoThrow(input: EndpointInput<Endpoint.BagCheckUpdateItemQuantity>) {
		return this.makeRequestNoThrow(Endpoint.BagCheckUpdateItemQuantity, input)
	}

	async restartBagCheck(input: EndpointInput<Endpoint.RestartBagCheck>) {
		return this.makeRequestWithThrow(Endpoint.RestartBagCheck, input)
	}
	async restartBagCheckNoThrow(input: EndpointInput<Endpoint.RestartBagCheck>) {
		return this.makeRequestNoThrow(Endpoint.RestartBagCheck, input)
	}

	async bagCheckApprove(input: EndpointInput<Endpoint.BagCheckApprove>) {
		return this.makeRequestWithThrow(Endpoint.BagCheckApprove, input)
	}
	async bagCheckApproveNoThrow(input: EndpointInput<Endpoint.BagCheckApprove>) {
		return this.makeRequestNoThrow(Endpoint.BagCheckApprove, input)
	}

	async bagCheckReject(input: EndpointInput<Endpoint.BagCheckReject>) {
		return this.makeRequestWithThrow(Endpoint.BagCheckReject, input)
	}
	async bagCheckRejectNoThrow(input: EndpointInput<Endpoint.BagCheckReject>) {
		return this.makeRequestNoThrow(Endpoint.BagCheckReject, input)
	}

	async listPromotions(input: EndpointInput<Endpoint.ListPromotions>) {
		return this.makeRequestWithThrow(Endpoint.ListPromotions, input)
	}
	async listPromotionsNoThrow(input: EndpointInput<Endpoint.ListPromotions>) {
		return this.makeRequestNoThrow(Endpoint.ListPromotions, input)
	}

	async createPromotion(input: EndpointInput<Endpoint.CreatePromotion>) {
		return this.makeRequestWithThrow(Endpoint.CreatePromotion, input)
	}
	async createPromotionNoThrow(input: EndpointInput<Endpoint.CreatePromotion>) {
		return this.makeRequestNoThrow(Endpoint.CreatePromotion, input)
	}

	async getPromotion(input: EndpointInput<Endpoint.GetPromotion>) {
		return this.makeRequestWithThrow(Endpoint.GetPromotion, input)
	}
	async getPromotionNoThrow(input: EndpointInput<Endpoint.GetPromotion>) {
		return this.makeRequestNoThrow(Endpoint.GetPromotion, input)
	}

	async deletePromotion(input: EndpointInput<Endpoint.DeletePromotion>) {
		return this.makeRequestWithThrow(Endpoint.DeletePromotion, input)
	}
	async deletePromotionNoThrow(input: EndpointInput<Endpoint.DeletePromotion>) {
		return this.makeRequestNoThrow(Endpoint.DeletePromotion, input)
	}

	async updatePromotion(input: EndpointInput<Endpoint.UpdatePromotion>) {
		return this.makeRequestWithThrow(Endpoint.UpdatePromotion, input)
	}
	async updatePromotionNoThrow(input: EndpointInput<Endpoint.UpdatePromotion>) {
		return this.makeRequestNoThrow(Endpoint.UpdatePromotion, input)
	}

	async listPromotionGroups(input: EndpointInput<Endpoint.ListPromotionGroups>) {
		return this.makeRequestWithThrow(Endpoint.ListPromotionGroups, input)
	}
	async listPromotionGroupsNoThrow(input: EndpointInput<Endpoint.ListPromotionGroups>) {
		return this.makeRequestNoThrow(Endpoint.ListPromotionGroups, input)
	}

	async listCountries(input: EndpointInput<Endpoint.ListCountries>) {
		return this.makeRequestWithThrow(Endpoint.ListCountries, input)
	}
	async listCountriesNoThrow(input: EndpointInput<Endpoint.ListCountries>) {
		return this.makeRequestNoThrow(Endpoint.ListCountries, input)
	}

	async getCountry(input: EndpointInput<Endpoint.GetCountry>) {
		return this.makeRequestWithThrow(Endpoint.GetCountry, input)
	}
	async getCountryNoThrow(input: EndpointInput<Endpoint.GetCountry>) {
		return this.makeRequestNoThrow(Endpoint.GetCountry, input)
	}

	async listCountryTaxRates(input: EndpointInput<Endpoint.ListCountryTaxRates>) {
		return this.makeRequestWithThrow(Endpoint.ListCountryTaxRates, input)
	}
	async listCountryTaxRatesNoThrow(input: EndpointInput<Endpoint.ListCountryTaxRates>) {
		return this.makeRequestNoThrow(Endpoint.ListCountryTaxRates, input)
	}

	async listTaxRates(input: EndpointInput<Endpoint.ListTaxRates>) {
		return this.makeRequestWithThrow(Endpoint.ListTaxRates, input)
	}
	async listTaxRatesNoThrow(input: EndpointInput<Endpoint.ListTaxRates>) {
		return this.makeRequestNoThrow(Endpoint.ListTaxRates, input)
	}

	async getAvailableExports(input: EndpointInput<Endpoint.GetAvailableExports>) {
		return this.makeRequestWithThrow(Endpoint.GetAvailableExports, input)
	}
	async getAvailableExportsNoThrow(input: EndpointInput<Endpoint.GetAvailableExports>) {
		return this.makeRequestNoThrow(Endpoint.GetAvailableExports, input)
	}

	async pricerProductExport(input: EndpointInput<Endpoint.PricerProductExport>) {
		return this.makeRequestWithThrow(Endpoint.PricerProductExport, input)
	}
	async pricerProductExportNoThrow(input: EndpointInput<Endpoint.PricerProductExport>) {
		return this.makeRequestNoThrow(Endpoint.PricerProductExport, input)
	}

	async vismaExport(input: EndpointInput<Endpoint.VismaExport>) {
		return this.makeRequestWithThrow(Endpoint.VismaExport, input)
	}
	async vismaExportNoThrow(input: EndpointInput<Endpoint.VismaExport>) {
		return this.makeRequestNoThrow(Endpoint.VismaExport, input)
	}

	async digiScaleProductExport(input: EndpointInput<Endpoint.DigiScaleProductExport>) {
		return this.makeRequestWithThrow(Endpoint.DigiScaleProductExport, input)
	}
	async digiScaleProductExportNoThrow(input: EndpointInput<Endpoint.DigiScaleProductExport>) {
		return this.makeRequestNoThrow(Endpoint.DigiScaleProductExport, input)
	}

	async getSAFTExportInfo(input: EndpointInput<Endpoint.GetSAFTExportInfo>) {
		return this.makeRequestWithThrow(Endpoint.GetSAFTExportInfo, input)
	}
	async getSAFTExportInfoNoThrow(input: EndpointInput<Endpoint.GetSAFTExportInfo>) {
		return this.makeRequestNoThrow(Endpoint.GetSAFTExportInfo, input)
	}

	async sAFTExportCashRegister(input: EndpointInput<Endpoint.SAFTExportCashRegister>) {
		return this.makeRequestWithThrow(Endpoint.SAFTExportCashRegister, input)
	}
	async sAFTExportCashRegisterNoThrow(input: EndpointInput<Endpoint.SAFTExportCashRegister>) {
		return this.makeRequestNoThrow(Endpoint.SAFTExportCashRegister, input)
	}

	async sAFTExportFinancial(input: EndpointInput<Endpoint.SAFTExportFinancial>) {
		return this.makeRequestWithThrow(Endpoint.SAFTExportFinancial, input)
	}
	async sAFTExportFinancialNoThrow(input: EndpointInput<Endpoint.SAFTExportFinancial>) {
		return this.makeRequestNoThrow(Endpoint.SAFTExportFinancial, input)
	}

	async listGiftCards(input: EndpointInput<Endpoint.ListGiftCards>) {
		return this.makeRequestWithThrow(Endpoint.ListGiftCards, input)
	}
	async listGiftCardsNoThrow(input: EndpointInput<Endpoint.ListGiftCards>) {
		return this.makeRequestNoThrow(Endpoint.ListGiftCards, input)
	}

	async createGiftCard(input: EndpointInput<Endpoint.CreateGiftCard>) {
		return this.makeRequestWithThrow(Endpoint.CreateGiftCard, input)
	}
	async createGiftCardNoThrow(input: EndpointInput<Endpoint.CreateGiftCard>) {
		return this.makeRequestNoThrow(Endpoint.CreateGiftCard, input)
	}

	async getGiftCard(input: EndpointInput<Endpoint.GetGiftCard>) {
		return this.makeRequestWithThrow(Endpoint.GetGiftCard, input)
	}
	async getGiftCardNoThrow(input: EndpointInput<Endpoint.GetGiftCard>) {
		return this.makeRequestNoThrow(Endpoint.GetGiftCard, input)
	}

	async updateGiftCard(input: EndpointInput<Endpoint.UpdateGiftCard>) {
		return this.makeRequestWithThrow(Endpoint.UpdateGiftCard, input)
	}
	async updateGiftCardNoThrow(input: EndpointInput<Endpoint.UpdateGiftCard>) {
		return this.makeRequestNoThrow(Endpoint.UpdateGiftCard, input)
	}

	async deleteGiftCard(input: EndpointInput<Endpoint.DeleteGiftCard>) {
		return this.makeRequestWithThrow(Endpoint.DeleteGiftCard, input)
	}
	async deleteGiftCardNoThrow(input: EndpointInput<Endpoint.DeleteGiftCard>) {
		return this.makeRequestNoThrow(Endpoint.DeleteGiftCard, input)
	}

	async listCompanies(input: EndpointInput<Endpoint.ListCompanies>) {
		return this.makeRequestWithThrow(Endpoint.ListCompanies, input)
	}
	async listCompaniesNoThrow(input: EndpointInput<Endpoint.ListCompanies>) {
		return this.makeRequestNoThrow(Endpoint.ListCompanies, input)
	}

	async createCompany(input: EndpointInput<Endpoint.CreateCompany>) {
		return this.makeRequestWithThrow(Endpoint.CreateCompany, input)
	}
	async createCompanyNoThrow(input: EndpointInput<Endpoint.CreateCompany>) {
		return this.makeRequestNoThrow(Endpoint.CreateCompany, input)
	}

	async getCompany(input: EndpointInput<Endpoint.GetCompany>) {
		return this.makeRequestWithThrow(Endpoint.GetCompany, input)
	}
	async getCompanyNoThrow(input: EndpointInput<Endpoint.GetCompany>) {
		return this.makeRequestNoThrow(Endpoint.GetCompany, input)
	}

	async updateCompany(input: EndpointInput<Endpoint.UpdateCompany>) {
		return this.makeRequestWithThrow(Endpoint.UpdateCompany, input)
	}
	async updateCompanyNoThrow(input: EndpointInput<Endpoint.UpdateCompany>) {
		return this.makeRequestNoThrow(Endpoint.UpdateCompany, input)
	}

	async deleteCompany(input: EndpointInput<Endpoint.DeleteCompany>) {
		return this.makeRequestWithThrow(Endpoint.DeleteCompany, input)
	}
	async deleteCompanyNoThrow(input: EndpointInput<Endpoint.DeleteCompany>) {
		return this.makeRequestNoThrow(Endpoint.DeleteCompany, input)
	}

	async createBusinessCard(input: EndpointInput<Endpoint.CreateBusinessCard>) {
		return this.makeRequestWithThrow(Endpoint.CreateBusinessCard, input)
	}
	async createBusinessCardNoThrow(input: EndpointInput<Endpoint.CreateBusinessCard>) {
		return this.makeRequestNoThrow(Endpoint.CreateBusinessCard, input)
	}

	async listCompanySettlements(input: EndpointInput<Endpoint.ListCompanySettlements>) {
		return this.makeRequestWithThrow(Endpoint.ListCompanySettlements, input)
	}
	async listCompanySettlementsNoThrow(input: EndpointInput<Endpoint.ListCompanySettlements>) {
		return this.makeRequestNoThrow(Endpoint.ListCompanySettlements, input)
	}

	async createSettlement(input: EndpointInput<Endpoint.CreateSettlement>) {
		return this.makeRequestWithThrow(Endpoint.CreateSettlement, input)
	}
	async createSettlementNoThrow(input: EndpointInput<Endpoint.CreateSettlement>) {
		return this.makeRequestNoThrow(Endpoint.CreateSettlement, input)
	}

	async listBusinessCards(input: EndpointInput<Endpoint.ListBusinessCards>) {
		return this.makeRequestWithThrow(Endpoint.ListBusinessCards, input)
	}
	async listBusinessCardsNoThrow(input: EndpointInput<Endpoint.ListBusinessCards>) {
		return this.makeRequestNoThrow(Endpoint.ListBusinessCards, input)
	}

	async getBusinessCard(input: EndpointInput<Endpoint.GetBusinessCard>) {
		return this.makeRequestWithThrow(Endpoint.GetBusinessCard, input)
	}
	async getBusinessCardNoThrow(input: EndpointInput<Endpoint.GetBusinessCard>) {
		return this.makeRequestNoThrow(Endpoint.GetBusinessCard, input)
	}

	async updateBusinessCard(input: EndpointInput<Endpoint.UpdateBusinessCard>) {
		return this.makeRequestWithThrow(Endpoint.UpdateBusinessCard, input)
	}
	async updateBusinessCardNoThrow(input: EndpointInput<Endpoint.UpdateBusinessCard>) {
		return this.makeRequestNoThrow(Endpoint.UpdateBusinessCard, input)
	}

	async deleteBusinessCard(input: EndpointInput<Endpoint.DeleteBusinessCard>) {
		return this.makeRequestWithThrow(Endpoint.DeleteBusinessCard, input)
	}
	async deleteBusinessCardNoThrow(input: EndpointInput<Endpoint.DeleteBusinessCard>) {
		return this.makeRequestNoThrow(Endpoint.DeleteBusinessCard, input)
	}

	async resetBusinessCardPin(input: EndpointInput<Endpoint.ResetBusinessCardPin>) {
		return this.makeRequestWithThrow(Endpoint.ResetBusinessCardPin, input)
	}
	async resetBusinessCardPinNoThrow(input: EndpointInput<Endpoint.ResetBusinessCardPin>) {
		return this.makeRequestNoThrow(Endpoint.ResetBusinessCardPin, input)
	}

	async verifyBusinessCardPin(input: EndpointInput<Endpoint.VerifyBusinessCardPin>) {
		return this.makeRequestWithThrow(Endpoint.VerifyBusinessCardPin, input)
	}
	async verifyBusinessCardPinNoThrow(input: EndpointInput<Endpoint.VerifyBusinessCardPin>) {
		return this.makeRequestNoThrow(Endpoint.VerifyBusinessCardPin, input)
	}

	async getBusinessCardTransactions(input: EndpointInput<Endpoint.GetBusinessCardTransactions>) {
		return this.makeRequestWithThrow(Endpoint.GetBusinessCardTransactions, input)
	}
	async getBusinessCardTransactionsNoThrow(input: EndpointInput<Endpoint.GetBusinessCardTransactions>) {
		return this.makeRequestNoThrow(Endpoint.GetBusinessCardTransactions, input)
	}

	async getSettlement(input: EndpointInput<Endpoint.GetSettlement>) {
		return this.makeRequestWithThrow(Endpoint.GetSettlement, input)
	}
	async getSettlementNoThrow(input: EndpointInput<Endpoint.GetSettlement>) {
		return this.makeRequestNoThrow(Endpoint.GetSettlement, input)
	}

	async markSettlementSettled(input: EndpointInput<Endpoint.MarkSettlementSettled>) {
		return this.makeRequestWithThrow(Endpoint.MarkSettlementSettled, input)
	}
	async markSettlementSettledNoThrow(input: EndpointInput<Endpoint.MarkSettlementSettled>) {
		return this.makeRequestNoThrow(Endpoint.MarkSettlementSettled, input)
	}

	async getAnalytics(input: EndpointInput<Endpoint.GetAnalytics>) {
		return this.makeRequestWithThrow(Endpoint.GetAnalytics, input)
	}
	async getAnalyticsNoThrow(input: EndpointInput<Endpoint.GetAnalytics>) {
		return this.makeRequestNoThrow(Endpoint.GetAnalytics, input)
	}

	async getBasketAnalysis(input: EndpointInput<Endpoint.GetBasketAnalysis>) {
		return this.makeRequestWithThrow(Endpoint.GetBasketAnalysis, input)
	}
	async getBasketAnalysisNoThrow(input: EndpointInput<Endpoint.GetBasketAnalysis>) {
		return this.makeRequestNoThrow(Endpoint.GetBasketAnalysis, input)
	}

	async getUserAnalytics(input: EndpointInput<Endpoint.GetUserAnalytics>) {
		return this.makeRequestWithThrow(Endpoint.GetUserAnalytics, input)
	}
	async getUserAnalyticsNoThrow(input: EndpointInput<Endpoint.GetUserAnalytics>) {
		return this.makeRequestNoThrow(Endpoint.GetUserAnalytics, input)
	}

	async getRevenueReport(input: EndpointInput<Endpoint.GetRevenueReport>) {
		return this.makeRequestWithThrow(Endpoint.GetRevenueReport, input)
	}
	async getRevenueReportNoThrow(input: EndpointInput<Endpoint.GetRevenueReport>) {
		return this.makeRequestNoThrow(Endpoint.GetRevenueReport, input)
	}

	async getProductReport(input: EndpointInput<Endpoint.GetProductReport>) {
		return this.makeRequestWithThrow(Endpoint.GetProductReport, input)
	}
	async getProductReportNoThrow(input: EndpointInput<Endpoint.GetProductReport>) {
		return this.makeRequestNoThrow(Endpoint.GetProductReport, input)
	}

	async listCustomers(input: EndpointInput<Endpoint.ListCustomers>) {
		return this.makeRequestWithThrow(Endpoint.ListCustomers, input)
	}
	async listCustomersNoThrow(input: EndpointInput<Endpoint.ListCustomers>) {
		return this.makeRequestNoThrow(Endpoint.ListCustomers, input)
	}

	async createCustomer(input: EndpointInput<Endpoint.CreateCustomer>) {
		return this.makeRequestWithThrow(Endpoint.CreateCustomer, input)
	}
	async createCustomerNoThrow(input: EndpointInput<Endpoint.CreateCustomer>) {
		return this.makeRequestNoThrow(Endpoint.CreateCustomer, input)
	}

	async getCustomer(input: EndpointInput<Endpoint.GetCustomer>) {
		return this.makeRequestWithThrow(Endpoint.GetCustomer, input)
	}
	async getCustomerNoThrow(input: EndpointInput<Endpoint.GetCustomer>) {
		return this.makeRequestNoThrow(Endpoint.GetCustomer, input)
	}

	async updateCustomer(input: EndpointInput<Endpoint.UpdateCustomer>) {
		return this.makeRequestWithThrow(Endpoint.UpdateCustomer, input)
	}
	async updateCustomerNoThrow(input: EndpointInput<Endpoint.UpdateCustomer>) {
		return this.makeRequestNoThrow(Endpoint.UpdateCustomer, input)
	}

	async revokeCustomerTokens(input: EndpointInput<Endpoint.RevokeCustomerTokens>) {
		return this.makeRequestWithThrow(Endpoint.RevokeCustomerTokens, input)
	}
	async revokeCustomerTokensNoThrow(input: EndpointInput<Endpoint.RevokeCustomerTokens>) {
		return this.makeRequestNoThrow(Endpoint.RevokeCustomerTokens, input)
	}

	async assumeCustomerToken(input: EndpointInput<Endpoint.AssumeCustomerToken>) {
		return this.makeRequestWithThrow(Endpoint.AssumeCustomerToken, input)
	}
	async assumeCustomerTokenNoThrow(input: EndpointInput<Endpoint.AssumeCustomerToken>) {
		return this.makeRequestNoThrow(Endpoint.AssumeCustomerToken, input)
	}

	async listCustomerGroups(input: EndpointInput<Endpoint.ListCustomerGroups>) {
		return this.makeRequestWithThrow(Endpoint.ListCustomerGroups, input)
	}
	async listCustomerGroupsNoThrow(input: EndpointInput<Endpoint.ListCustomerGroups>) {
		return this.makeRequestNoThrow(Endpoint.ListCustomerGroups, input)
	}

	async createCustomerGroup(input: EndpointInput<Endpoint.CreateCustomerGroup>) {
		return this.makeRequestWithThrow(Endpoint.CreateCustomerGroup, input)
	}
	async createCustomerGroupNoThrow(input: EndpointInput<Endpoint.CreateCustomerGroup>) {
		return this.makeRequestNoThrow(Endpoint.CreateCustomerGroup, input)
	}

	async getCustomerGroup(input: EndpointInput<Endpoint.GetCustomerGroup>) {
		return this.makeRequestWithThrow(Endpoint.GetCustomerGroup, input)
	}
	async getCustomerGroupNoThrow(input: EndpointInput<Endpoint.GetCustomerGroup>) {
		return this.makeRequestNoThrow(Endpoint.GetCustomerGroup, input)
	}

	async updateCustomerGroup(input: EndpointInput<Endpoint.UpdateCustomerGroup>) {
		return this.makeRequestWithThrow(Endpoint.UpdateCustomerGroup, input)
	}
	async updateCustomerGroupNoThrow(input: EndpointInput<Endpoint.UpdateCustomerGroup>) {
		return this.makeRequestNoThrow(Endpoint.UpdateCustomerGroup, input)
	}

	async deleteCustomerGroup(input: EndpointInput<Endpoint.DeleteCustomerGroup>) {
		return this.makeRequestWithThrow(Endpoint.DeleteCustomerGroup, input)
	}
	async deleteCustomerGroupNoThrow(input: EndpointInput<Endpoint.DeleteCustomerGroup>) {
		return this.makeRequestNoThrow(Endpoint.DeleteCustomerGroup, input)
	}

	async listLoyaltyCards(input: EndpointInput<Endpoint.ListLoyaltyCards>) {
		return this.makeRequestWithThrow(Endpoint.ListLoyaltyCards, input)
	}
	async listLoyaltyCardsNoThrow(input: EndpointInput<Endpoint.ListLoyaltyCards>) {
		return this.makeRequestNoThrow(Endpoint.ListLoyaltyCards, input)
	}

	async createLoyaltyCard(input: EndpointInput<Endpoint.CreateLoyaltyCard>) {
		return this.makeRequestWithThrow(Endpoint.CreateLoyaltyCard, input)
	}
	async createLoyaltyCardNoThrow(input: EndpointInput<Endpoint.CreateLoyaltyCard>) {
		return this.makeRequestNoThrow(Endpoint.CreateLoyaltyCard, input)
	}

	async getLoyaltyCard(input: EndpointInput<Endpoint.GetLoyaltyCard>) {
		return this.makeRequestWithThrow(Endpoint.GetLoyaltyCard, input)
	}
	async getLoyaltyCardNoThrow(input: EndpointInput<Endpoint.GetLoyaltyCard>) {
		return this.makeRequestNoThrow(Endpoint.GetLoyaltyCard, input)
	}

	async updateLoyaltyCard(input: EndpointInput<Endpoint.UpdateLoyaltyCard>) {
		return this.makeRequestWithThrow(Endpoint.UpdateLoyaltyCard, input)
	}
	async updateLoyaltyCardNoThrow(input: EndpointInput<Endpoint.UpdateLoyaltyCard>) {
		return this.makeRequestNoThrow(Endpoint.UpdateLoyaltyCard, input)
	}

	async deleteLoyaltyCard(input: EndpointInput<Endpoint.DeleteLoyaltyCard>) {
		return this.makeRequestWithThrow(Endpoint.DeleteLoyaltyCard, input)
	}
	async deleteLoyaltyCardNoThrow(input: EndpointInput<Endpoint.DeleteLoyaltyCard>) {
		return this.makeRequestNoThrow(Endpoint.DeleteLoyaltyCard, input)
	}

	async getLoyaltyCardRedemptions(input: EndpointInput<Endpoint.GetLoyaltyCardRedemptions>) {
		return this.makeRequestWithThrow(Endpoint.GetLoyaltyCardRedemptions, input)
	}
	async getLoyaltyCardRedemptionsNoThrow(input: EndpointInput<Endpoint.GetLoyaltyCardRedemptions>) {
		return this.makeRequestNoThrow(Endpoint.GetLoyaltyCardRedemptions, input)
	}

	async getDashboardFeatures(input: EndpointInput<Endpoint.GetDashboardFeatures>) {
		return this.makeRequestWithThrow(Endpoint.GetDashboardFeatures, input)
	}
	async getDashboardFeaturesNoThrow(input: EndpointInput<Endpoint.GetDashboardFeatures>) {
		return this.makeRequestNoThrow(Endpoint.GetDashboardFeatures, input)
	}

	async submitDeveloperFeedback(input: EndpointInput<Endpoint.SubmitDeveloperFeedback>) {
		return this.makeRequestWithThrow(Endpoint.SubmitDeveloperFeedback, input)
	}
	async submitDeveloperFeedbackNoThrow(input: EndpointInput<Endpoint.SubmitDeveloperFeedback>) {
		return this.makeRequestNoThrow(Endpoint.SubmitDeveloperFeedback, input)
	}

	async listInventoryLocations(input: EndpointInput<Endpoint.ListInventoryLocations>) {
		return this.makeRequestWithThrow(Endpoint.ListInventoryLocations, input)
	}
	async listInventoryLocationsNoThrow(input: EndpointInput<Endpoint.ListInventoryLocations>) {
		return this.makeRequestNoThrow(Endpoint.ListInventoryLocations, input)
	}

	async createInventoryLocation(input: EndpointInput<Endpoint.CreateInventoryLocation>) {
		return this.makeRequestWithThrow(Endpoint.CreateInventoryLocation, input)
	}
	async createInventoryLocationNoThrow(input: EndpointInput<Endpoint.CreateInventoryLocation>) {
		return this.makeRequestNoThrow(Endpoint.CreateInventoryLocation, input)
	}

	async searchInventoryVariants(input: EndpointInput<Endpoint.SearchInventoryVariants>) {
		return this.makeRequestWithThrow(Endpoint.SearchInventoryVariants, input)
	}
	async searchInventoryVariantsNoThrow(input: EndpointInput<Endpoint.SearchInventoryVariants>) {
		return this.makeRequestNoThrow(Endpoint.SearchInventoryVariants, input)
	}

	async getInventoryLocation(input: EndpointInput<Endpoint.GetInventoryLocation>) {
		return this.makeRequestWithThrow(Endpoint.GetInventoryLocation, input)
	}
	async getInventoryLocationNoThrow(input: EndpointInput<Endpoint.GetInventoryLocation>) {
		return this.makeRequestNoThrow(Endpoint.GetInventoryLocation, input)
	}

	async updateInventoryLocation(input: EndpointInput<Endpoint.UpdateInventoryLocation>) {
		return this.makeRequestWithThrow(Endpoint.UpdateInventoryLocation, input)
	}
	async updateInventoryLocationNoThrow(input: EndpointInput<Endpoint.UpdateInventoryLocation>) {
		return this.makeRequestNoThrow(Endpoint.UpdateInventoryLocation, input)
	}

	async provisionInventoryLocationLevels(input: EndpointInput<Endpoint.ProvisionInventoryLocationLevels>) {
		return this.makeRequestWithThrow(Endpoint.ProvisionInventoryLocationLevels, input)
	}
	async provisionInventoryLocationLevelsNoThrow(input: EndpointInput<Endpoint.ProvisionInventoryLocationLevels>) {
		return this.makeRequestNoThrow(Endpoint.ProvisionInventoryLocationLevels, input)
	}

	async getInventorySettings(input: EndpointInput<Endpoint.GetInventorySettings>) {
		return this.makeRequestWithThrow(Endpoint.GetInventorySettings, input)
	}
	async getInventorySettingsNoThrow(input: EndpointInput<Endpoint.GetInventorySettings>) {
		return this.makeRequestNoThrow(Endpoint.GetInventorySettings, input)
	}

	async updateInventorySettings(input: EndpointInput<Endpoint.UpdateInventorySettings>) {
		return this.makeRequestWithThrow(Endpoint.UpdateInventorySettings, input)
	}
	async updateInventorySettingsNoThrow(input: EndpointInput<Endpoint.UpdateInventorySettings>) {
		return this.makeRequestNoThrow(Endpoint.UpdateInventorySettings, input)
	}

	async listInventoryLevels(input: EndpointInput<Endpoint.ListInventoryLevels>) {
		return this.makeRequestWithThrow(Endpoint.ListInventoryLevels, input)
	}
	async listInventoryLevelsNoThrow(input: EndpointInput<Endpoint.ListInventoryLevels>) {
		return this.makeRequestNoThrow(Endpoint.ListInventoryLevels, input)
	}

	async getInventorySummary(input: EndpointInput<Endpoint.GetInventorySummary>) {
		return this.makeRequestWithThrow(Endpoint.GetInventorySummary, input)
	}
	async getInventorySummaryNoThrow(input: EndpointInput<Endpoint.GetInventorySummary>) {
		return this.makeRequestNoThrow(Endpoint.GetInventorySummary, input)
	}

	async getInventoryLevel(input: EndpointInput<Endpoint.GetInventoryLevel>) {
		return this.makeRequestWithThrow(Endpoint.GetInventoryLevel, input)
	}
	async getInventoryLevelNoThrow(input: EndpointInput<Endpoint.GetInventoryLevel>) {
		return this.makeRequestNoThrow(Endpoint.GetInventoryLevel, input)
	}

	async updateInventoryLevel(input: EndpointInput<Endpoint.UpdateInventoryLevel>) {
		return this.makeRequestWithThrow(Endpoint.UpdateInventoryLevel, input)
	}
	async updateInventoryLevelNoThrow(input: EndpointInput<Endpoint.UpdateInventoryLevel>) {
		return this.makeRequestNoThrow(Endpoint.UpdateInventoryLevel, input)
	}

	async adjustInventoryLevel(input: EndpointInput<Endpoint.AdjustInventoryLevel>) {
		return this.makeRequestWithThrow(Endpoint.AdjustInventoryLevel, input)
	}
	async adjustInventoryLevelNoThrow(input: EndpointInput<Endpoint.AdjustInventoryLevel>) {
		return this.makeRequestNoThrow(Endpoint.AdjustInventoryLevel, input)
	}

	async listStockMovements(input: EndpointInput<Endpoint.ListStockMovements>) {
		return this.makeRequestWithThrow(Endpoint.ListStockMovements, input)
	}
	async listStockMovementsNoThrow(input: EndpointInput<Endpoint.ListStockMovements>) {
		return this.makeRequestNoThrow(Endpoint.ListStockMovements, input)
	}

	async listSuppliers(input: EndpointInput<Endpoint.ListSuppliers>) {
		return this.makeRequestWithThrow(Endpoint.ListSuppliers, input)
	}
	async listSuppliersNoThrow(input: EndpointInput<Endpoint.ListSuppliers>) {
		return this.makeRequestNoThrow(Endpoint.ListSuppliers, input)
	}

	async createSupplier(input: EndpointInput<Endpoint.CreateSupplier>) {
		return this.makeRequestWithThrow(Endpoint.CreateSupplier, input)
	}
	async createSupplierNoThrow(input: EndpointInput<Endpoint.CreateSupplier>) {
		return this.makeRequestNoThrow(Endpoint.CreateSupplier, input)
	}

	async getSupplier(input: EndpointInput<Endpoint.GetSupplier>) {
		return this.makeRequestWithThrow(Endpoint.GetSupplier, input)
	}
	async getSupplierNoThrow(input: EndpointInput<Endpoint.GetSupplier>) {
		return this.makeRequestNoThrow(Endpoint.GetSupplier, input)
	}

	async updateSupplier(input: EndpointInput<Endpoint.UpdateSupplier>) {
		return this.makeRequestWithThrow(Endpoint.UpdateSupplier, input)
	}
	async updateSupplierNoThrow(input: EndpointInput<Endpoint.UpdateSupplier>) {
		return this.makeRequestNoThrow(Endpoint.UpdateSupplier, input)
	}

	async listPurchaseOrders(input: EndpointInput<Endpoint.ListPurchaseOrders>) {
		return this.makeRequestWithThrow(Endpoint.ListPurchaseOrders, input)
	}
	async listPurchaseOrdersNoThrow(input: EndpointInput<Endpoint.ListPurchaseOrders>) {
		return this.makeRequestNoThrow(Endpoint.ListPurchaseOrders, input)
	}

	async createPurchaseOrder(input: EndpointInput<Endpoint.CreatePurchaseOrder>) {
		return this.makeRequestWithThrow(Endpoint.CreatePurchaseOrder, input)
	}
	async createPurchaseOrderNoThrow(input: EndpointInput<Endpoint.CreatePurchaseOrder>) {
		return this.makeRequestNoThrow(Endpoint.CreatePurchaseOrder, input)
	}

	async createReplenishmentPurchaseOrders(input: EndpointInput<Endpoint.CreateReplenishmentPurchaseOrders>) {
		return this.makeRequestWithThrow(Endpoint.CreateReplenishmentPurchaseOrders, input)
	}
	async createReplenishmentPurchaseOrdersNoThrow(input: EndpointInput<Endpoint.CreateReplenishmentPurchaseOrders>) {
		return this.makeRequestNoThrow(Endpoint.CreateReplenishmentPurchaseOrders, input)
	}

	async getPurchaseOrder(input: EndpointInput<Endpoint.GetPurchaseOrder>) {
		return this.makeRequestWithThrow(Endpoint.GetPurchaseOrder, input)
	}
	async getPurchaseOrderNoThrow(input: EndpointInput<Endpoint.GetPurchaseOrder>) {
		return this.makeRequestNoThrow(Endpoint.GetPurchaseOrder, input)
	}

	async receivePurchaseOrder(input: EndpointInput<Endpoint.ReceivePurchaseOrder>) {
		return this.makeRequestWithThrow(Endpoint.ReceivePurchaseOrder, input)
	}
	async receivePurchaseOrderNoThrow(input: EndpointInput<Endpoint.ReceivePurchaseOrder>) {
		return this.makeRequestNoThrow(Endpoint.ReceivePurchaseOrder, input)
	}

	async markPurchaseOrderOrdered(input: EndpointInput<Endpoint.MarkPurchaseOrderOrdered>) {
		return this.makeRequestWithThrow(Endpoint.MarkPurchaseOrderOrdered, input)
	}
	async markPurchaseOrderOrderedNoThrow(input: EndpointInput<Endpoint.MarkPurchaseOrderOrdered>) {
		return this.makeRequestNoThrow(Endpoint.MarkPurchaseOrderOrdered, input)
	}

	async cancelPurchaseOrder(input: EndpointInput<Endpoint.CancelPurchaseOrder>) {
		return this.makeRequestWithThrow(Endpoint.CancelPurchaseOrder, input)
	}
	async cancelPurchaseOrderNoThrow(input: EndpointInput<Endpoint.CancelPurchaseOrder>) {
		return this.makeRequestNoThrow(Endpoint.CancelPurchaseOrder, input)
	}

	async listInventoryTransfers(input: EndpointInput<Endpoint.ListInventoryTransfers>) {
		return this.makeRequestWithThrow(Endpoint.ListInventoryTransfers, input)
	}
	async listInventoryTransfersNoThrow(input: EndpointInput<Endpoint.ListInventoryTransfers>) {
		return this.makeRequestNoThrow(Endpoint.ListInventoryTransfers, input)
	}

	async createInventoryTransfer(input: EndpointInput<Endpoint.CreateInventoryTransfer>) {
		return this.makeRequestWithThrow(Endpoint.CreateInventoryTransfer, input)
	}
	async createInventoryTransferNoThrow(input: EndpointInput<Endpoint.CreateInventoryTransfer>) {
		return this.makeRequestNoThrow(Endpoint.CreateInventoryTransfer, input)
	}

	async getInventoryTransfer(input: EndpointInput<Endpoint.GetInventoryTransfer>) {
		return this.makeRequestWithThrow(Endpoint.GetInventoryTransfer, input)
	}
	async getInventoryTransferNoThrow(input: EndpointInput<Endpoint.GetInventoryTransfer>) {
		return this.makeRequestNoThrow(Endpoint.GetInventoryTransfer, input)
	}

	async shipInventoryTransfer(input: EndpointInput<Endpoint.ShipInventoryTransfer>) {
		return this.makeRequestWithThrow(Endpoint.ShipInventoryTransfer, input)
	}
	async shipInventoryTransferNoThrow(input: EndpointInput<Endpoint.ShipInventoryTransfer>) {
		return this.makeRequestNoThrow(Endpoint.ShipInventoryTransfer, input)
	}

	async receiveInventoryTransfer(input: EndpointInput<Endpoint.ReceiveInventoryTransfer>) {
		return this.makeRequestWithThrow(Endpoint.ReceiveInventoryTransfer, input)
	}
	async receiveInventoryTransferNoThrow(input: EndpointInput<Endpoint.ReceiveInventoryTransfer>) {
		return this.makeRequestNoThrow(Endpoint.ReceiveInventoryTransfer, input)
	}

	async cancelInventoryTransfer(input: EndpointInput<Endpoint.CancelInventoryTransfer>) {
		return this.makeRequestWithThrow(Endpoint.CancelInventoryTransfer, input)
	}
	async cancelInventoryTransferNoThrow(input: EndpointInput<Endpoint.CancelInventoryTransfer>) {
		return this.makeRequestNoThrow(Endpoint.CancelInventoryTransfer, input)
	}

	async getInventoryReportSummary(input: EndpointInput<Endpoint.GetInventoryReportSummary>) {
		return this.makeRequestWithThrow(Endpoint.GetInventoryReportSummary, input)
	}
	async getInventoryReportSummaryNoThrow(input: EndpointInput<Endpoint.GetInventoryReportSummary>) {
		return this.makeRequestNoThrow(Endpoint.GetInventoryReportSummary, input)
	}

	async listVariantSuppliers(input: EndpointInput<Endpoint.ListVariantSuppliers>) {
		return this.makeRequestWithThrow(Endpoint.ListVariantSuppliers, input)
	}
	async listVariantSuppliersNoThrow(input: EndpointInput<Endpoint.ListVariantSuppliers>) {
		return this.makeRequestNoThrow(Endpoint.ListVariantSuppliers, input)
	}

	async upsertVariantSupplier(input: EndpointInput<Endpoint.UpsertVariantSupplier>) {
		return this.makeRequestWithThrow(Endpoint.UpsertVariantSupplier, input)
	}
	async upsertVariantSupplierNoThrow(input: EndpointInput<Endpoint.UpsertVariantSupplier>) {
		return this.makeRequestNoThrow(Endpoint.UpsertVariantSupplier, input)
	}

	async setPreferredVariantSupplier(input: EndpointInput<Endpoint.SetPreferredVariantSupplier>) {
		return this.makeRequestWithThrow(Endpoint.SetPreferredVariantSupplier, input)
	}
	async setPreferredVariantSupplierNoThrow(input: EndpointInput<Endpoint.SetPreferredVariantSupplier>) {
		return this.makeRequestNoThrow(Endpoint.SetPreferredVariantSupplier, input)
	}

	async deleteVariantSupplier(input: EndpointInput<Endpoint.DeleteVariantSupplier>) {
		return this.makeRequestWithThrow(Endpoint.DeleteVariantSupplier, input)
	}
	async deleteVariantSupplierNoThrow(input: EndpointInput<Endpoint.DeleteVariantSupplier>) {
		return this.makeRequestNoThrow(Endpoint.DeleteVariantSupplier, input)
	}

	async listIntegrations(input: EndpointInput<Endpoint.ListIntegrations>) {
		return this.makeRequestWithThrow(Endpoint.ListIntegrations, input)
	}
	async listIntegrationsNoThrow(input: EndpointInput<Endpoint.ListIntegrations>) {
		return this.makeRequestNoThrow(Endpoint.ListIntegrations, input)
	}

	async getTeyaIntegration(input: EndpointInput<Endpoint.GetTeyaIntegration>) {
		return this.makeRequestWithThrow(Endpoint.GetTeyaIntegration, input)
	}
	async getTeyaIntegrationNoThrow(input: EndpointInput<Endpoint.GetTeyaIntegration>) {
		return this.makeRequestNoThrow(Endpoint.GetTeyaIntegration, input)
	}

	async createTeyaConnectUrl(input: EndpointInput<Endpoint.CreateTeyaConnectUrl>) {
		return this.makeRequestWithThrow(Endpoint.CreateTeyaConnectUrl, input)
	}
	async createTeyaConnectUrlNoThrow(input: EndpointInput<Endpoint.CreateTeyaConnectUrl>) {
		return this.makeRequestNoThrow(Endpoint.CreateTeyaConnectUrl, input)
	}

	async disconnectTeya(input: EndpointInput<Endpoint.DisconnectTeya>) {
		return this.makeRequestWithThrow(Endpoint.DisconnectTeya, input)
	}
	async disconnectTeyaNoThrow(input: EndpointInput<Endpoint.DisconnectTeya>) {
		return this.makeRequestNoThrow(Endpoint.DisconnectTeya, input)
	}

	async listTeyaStores(input: EndpointInput<Endpoint.ListTeyaStores>) {
		return this.makeRequestWithThrow(Endpoint.ListTeyaStores, input)
	}
	async listTeyaStoresNoThrow(input: EndpointInput<Endpoint.ListTeyaStores>) {
		return this.makeRequestNoThrow(Endpoint.ListTeyaStores, input)
	}

	async listTeyaTerminals(input: EndpointInput<Endpoint.ListTeyaTerminals>) {
		return this.makeRequestWithThrow(Endpoint.ListTeyaTerminals, input)
	}
	async listTeyaTerminalsNoThrow(input: EndpointInput<Endpoint.ListTeyaTerminals>) {
		return this.makeRequestNoThrow(Endpoint.ListTeyaTerminals, input)
	}

	async registerRapydDevice(input: EndpointInput<Endpoint.RegisterRapydDevice>) {
		return this.makeRequestWithThrow(Endpoint.RegisterRapydDevice, input)
	}
	async registerRapydDeviceNoThrow(input: EndpointInput<Endpoint.RegisterRapydDevice>) {
		return this.makeRequestNoThrow(Endpoint.RegisterRapydDevice, input)
	}

	async pollRapydDeviceToken(input: EndpointInput<Endpoint.PollRapydDeviceToken>) {
		return this.makeRequestWithThrow(Endpoint.PollRapydDeviceToken, input)
	}
	async pollRapydDeviceTokenNoThrow(input: EndpointInput<Endpoint.PollRapydDeviceToken>) {
		return this.makeRequestNoThrow(Endpoint.PollRapydDeviceToken, input)
	}

	async getIntegrationConfig(input: EndpointInput<Endpoint.GetIntegrationConfig>) {
		return this.makeRequestWithThrow(Endpoint.GetIntegrationConfig, input)
	}
	async getIntegrationConfigNoThrow(input: EndpointInput<Endpoint.GetIntegrationConfig>) {
		return this.makeRequestNoThrow(Endpoint.GetIntegrationConfig, input)
	}

	async putIntegrationGlobalConfig(input: EndpointInput<Endpoint.PutIntegrationGlobalConfig>) {
		return this.makeRequestWithThrow(Endpoint.PutIntegrationGlobalConfig, input)
	}
	async putIntegrationGlobalConfigNoThrow(input: EndpointInput<Endpoint.PutIntegrationGlobalConfig>) {
		return this.makeRequestNoThrow(Endpoint.PutIntegrationGlobalConfig, input)
	}

	async createIntegrationOverride(input: EndpointInput<Endpoint.CreateIntegrationOverride>) {
		return this.makeRequestWithThrow(Endpoint.CreateIntegrationOverride, input)
	}
	async createIntegrationOverrideNoThrow(input: EndpointInput<Endpoint.CreateIntegrationOverride>) {
		return this.makeRequestNoThrow(Endpoint.CreateIntegrationOverride, input)
	}

	async updateIntegrationOverride(input: EndpointInput<Endpoint.UpdateIntegrationOverride>) {
		return this.makeRequestWithThrow(Endpoint.UpdateIntegrationOverride, input)
	}
	async updateIntegrationOverrideNoThrow(input: EndpointInput<Endpoint.UpdateIntegrationOverride>) {
		return this.makeRequestNoThrow(Endpoint.UpdateIntegrationOverride, input)
	}

	async deleteIntegrationOverride(input: EndpointInput<Endpoint.DeleteIntegrationOverride>) {
		return this.makeRequestWithThrow(Endpoint.DeleteIntegrationOverride, input)
	}
	async deleteIntegrationOverrideNoThrow(input: EndpointInput<Endpoint.DeleteIntegrationOverride>) {
		return this.makeRequestNoThrow(Endpoint.DeleteIntegrationOverride, input)
	}

	async getBillingPlan(input: EndpointInput<Endpoint.GetBillingPlan>) {
		return this.makeRequestWithThrow(Endpoint.GetBillingPlan, input)
	}
	async getBillingPlanNoThrow(input: EndpointInput<Endpoint.GetBillingPlan>) {
		return this.makeRequestNoThrow(Endpoint.GetBillingPlan, input)
	}

	async acceptBillingPlan(input: EndpointInput<Endpoint.AcceptBillingPlan>) {
		return this.makeRequestWithThrow(Endpoint.AcceptBillingPlan, input)
	}
	async acceptBillingPlanNoThrow(input: EndpointInput<Endpoint.AcceptBillingPlan>) {
		return this.makeRequestNoThrow(Endpoint.AcceptBillingPlan, input)
	}

	async getAppUpdateBranches(input: EndpointInput<Endpoint.GetAppUpdateBranches>) {
		return this.makeRequestWithThrow(Endpoint.GetAppUpdateBranches, input)
	}
	async getAppUpdateBranchesNoThrow(input: EndpointInput<Endpoint.GetAppUpdateBranches>) {
		return this.makeRequestNoThrow(Endpoint.GetAppUpdateBranches, input)
	}

	async getAppUpdates(input: EndpointInput<Endpoint.GetAppUpdates>) {
		return this.makeRequestWithThrow(Endpoint.GetAppUpdates, input)
	}
	async getAppUpdatesNoThrow(input: EndpointInput<Endpoint.GetAppUpdates>) {
		return this.makeRequestNoThrow(Endpoint.GetAppUpdates, input)
	}
}
